<template>
  <Dialog
    v-model:visible="dialogVisible"
    @update:visible="onDialogVisibleChange"
    :header="false"
    modal
    :style="{ width: modalWidth }"
    :dismissableMask="true"
    :draggable="false"
    :closable="false"
    class="conectados-detalle-modal"
  >


    <!-- ── Breadcrumb ── -->
    <transition name="cdm-fade-slide">
      <div v-if="selectedStream || selectedDevice" class="cdm-breadcrumb">
        <button class="cdm-breadcrumb-item" @click="irARaices">
          <i class="pi pi-th-large"></i>
          <span>Canales</span>
        </button>
        <i class="pi pi-chevron-right cdm-breadcrumb-sep"></i>
        <button
          v-if="selectedStream"
          class="cdm-breadcrumb-item"
          :class="{ 'is-active': !selectedDevice }"
          :disabled="!selectedDevice"
          @click="volverADispositivos"
        >
          <i class="pi pi-building"></i>
          <span>{{ radioName || selectedStream }}</span>
        </button>
        <template v-if="selectedDevice">
          <i class="pi pi-chevron-right cdm-breadcrumb-sep"></i>
          <span class="cdm-breadcrumb-item is-active">
            <i class="pi pi-mobile"></i>
            <span class="cdm-breadcrumb-device-id">{{ displayDeviceId(selectedDevice) }}</span>
          </span>
        </template>
      </div>
    </transition>

    <!-- ── Content area (with view transitions) ── -->
    <div ref="cdmBody" class="cdm-body">
      <transition name="cdm-view" mode="out-in">

      <!-- Loading skeleton (card-shaped) -->
      <div v-if="loading" key="loading" class="cdm-skeleton">
        <div class="cdm-skel-card" v-for="i in [1,2,3]" :key="i">
          <div class="cdm-skel-card-left">
            <div class="cdm-skel-circle"></div>
            <div class="cdm-skel-bar-v"></div>
          </div>
          <div class="cdm-skel-card-body">
            <div class="cdm-skel-line cdm-skel-line--lg"></div>
            <div class="cdm-skel-line cdm-skel-line--sm"></div>
            <div class="cdm-skel-line cdm-skel-line--md"></div>
          </div>
          <div class="cdm-skel-card-right">
            <div class="cdm-skel-line cdm-skel-line--num"></div>
            <div class="cdm-skel-line cdm-skel-line--pct"></div>
            <div class="cdm-skel-bar-track"><div class="cdm-skel-bar-fill"></div></div>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" key="error" class="cdm-state cdm-state--error">
        <div class="cdm-state-icon">
          <i class="pi pi-exclamation-triangle"></i>
        </div>
        <p class="cdm-state-text">{{ error }}</p>
        <div class="cdm-state-actions">
          <button class="cdm-state-btn" @click="recargarVistaActual">
            <i class="pi pi-refresh mr-1"></i>Reintentar
          </button>
          <button class="cdm-state-btn" @click="irARaices">
            <i class="pi pi-arrow-left mr-1"></i>Volver
          </button>
        </div>
      </div>        <!-- ── View 1: Sucursales con métricas ── -->
      <div v-else-if="!selectedStream" key="sucursales">
        <!-- Hero -->
        <div class="kpi-mus-hero mb-3" style="background:linear-gradient(135deg,rgba(16,185,129,0.15) 0%,rgba(16,185,129,0.05) 100%);border:1px solid rgba(16,185,129,0.2);border-radius:12px;padding:1.1rem 0.75rem 0.9rem">
          <div class="kpi-mus-hero-row" style="justify-content:center;position:relative">
            <button
              class="cdm-refresh-btn"
              :class="{ 'is-loading': loading }"
              @click="recargarVistaActual"
              :disabled="loading"
              :title="recargarLabel"
              aria-label="Recargar"
              style="position:absolute;top:-6px;right:-4px;z-index:1;width:26px;height:26px;font-size:0.75rem"
            >
              <i class="pi pi-refresh"></i>
            </button>
            <button
              class="cdm-refresh-btn"
              @click="toggleAutoRefresh"
              :title="autoRefreshEnabled ? 'Auto-actualización activa — haz clic para pausar' : 'Auto-actualización pausada — haz clic para reanudar'"
              :aria-label="autoRefreshEnabled ? 'Pausar auto-actualización' : 'Reanudar auto-actualización'"
              style="position:absolute;top:-6px;right:28px;z-index:1;width:26px;height:26px;font-size:0.75rem"
            >
              <i class="pi" :class="autoRefreshEnabled ? 'pi-pause' : 'pi-play'"></i>
            </button>
            <div class="kpi-mus-hero-left" style="text-align:center;width:100%">
              <span class="kpi-card-label">
                <i class="pi pi-sync mr-1" style="font-size:0.7rem;color:#10b981"></i>
                Conectados ahora
                <span class="kpi-live-dot"></span>
              </span>
              <div class="kpi-mus-hero-pct" style="font-size:2.6rem">
                {{ (conectadosAhora?.total || 0).toLocaleString() }}
                <span style="font-size:1rem;color:var(--text-color-secondary,#94a3b8);font-weight:500">/{{ heroTotalSucursales.toLocaleString() }}</span>
              </div>
              <span v-if="conectadosAhora?.actualizado_en" class="kpi-card-sub-muted" style="display:block;margin-top:2px">
                <i class="pi pi-sync mr-1" style="font-size:0.68rem"></i>{{ timeAgo(conectadosAhora.actualizado_en) }}
              </span>
              <div class="cdm-hero-bar" style="margin-top:0.75rem;max-width:360px;margin-left:auto;margin-right:auto">
                <div class="cdm-hero-bar-track">
                  <div class="cdm-hero-bar-fill" :class="conexionPctGlobal >= 90 ? '' : conexionPctGlobal >= 50 ? 'is-yellow' : 'is-red'" :style="{ width: Math.max(4, conexionPctGlobal) + '%' }"></div>
                </div>
                <span class="cdm-hero-bar-pct" :class="conexionPctGlobal >= 90 ? '' : conexionPctGlobal >= 50 ? 'is-yellow' : 'is-red'">{{ conexionPctGlobal }}% conexión</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Insight bar -->
        <div class="cdm-insight-bar">
          <template v-if="canalesOptimos.length || canalesRiesgo.length || canalesCriticos.length">
            <div v-if="canalesCriticos.length" class="cdm-insight-item">
              <span class="cdm-insight-dot is-red"></span>
              <span>{{ canalesCriticos.length }} criticos</span>
            </div>
            <div v-if="canalesRiesgo.length" class="cdm-insight-item">
              <span class="cdm-insight-dot is-yellow"></span>
              <span>{{ canalesRiesgo.length }} en riesgo</span>
            </div>
            <div v-if="canalesOptimos.length" class="cdm-insight-item">
              <span class="cdm-insight-dot is-green"></span>
              <span>{{ canalesOptimos.length }} optimos</span>
            </div>
          </template>
          <div v-if="canalesSorted.length > 0 && !canalesCriticos.length && !canalesRiesgo.length" class="cdm-insight-item is-success">
            <i class="pi pi-check-circle" style="font-size:0.6rem"></i>
            <span>Todos los canales optimos</span>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="cdm-toolbar">
          <div class="cdm-toolbar-row">
            <div class="cdm-search">
              <i class="pi pi-search cdm-search-icon"></i>
              <input v-model="searchQuery" type="text" class="cdm-search-input" placeholder="Buscar canal..." />
            </div>
            <div class="cdm-view-toggle">
              <button class="cdm-view-btn" :class="{ 'is-active': viewMode === 'list' }" @click="viewMode = 'list'" title="Vista lista">
                <i class="pi pi-list"></i>
              </button>
              <button class="cdm-view-btn" :class="{ 'is-active': viewMode === 'grid' }" @click="viewMode = 'grid'" title="Vista cuadricula">
                <i class="pi pi-th-large"></i>
              </button>
            </div>
          </div>
          <div class="cdm-toolbar-row cdm-toolbar-row--filters">
            <div class="cdm-chip-group" role="group" aria-label="Filtrar por estado">
              <label class="cdm-chip" :class="{ 'is-active': filterState === 'all' }">
                <input type="radio" name="filterState" @change="filterState = 'all'" />
                <span>Todos ({{ canalesSorted.length }})</span>
              </label>
              <label class="cdm-chip is-optimo" :class="{ 'is-active': filterState === 'optimo' }">
                <input type="radio" name="filterState" @change="filterState = 'optimo'" />
                <span>Optimos ({{ canalesOptimos.length }})</span>
              </label>
              <label class="cdm-chip is-riesgo" :class="{ 'is-active': filterState === 'riesgo' }">
                <input type="radio" name="filterState" @change="filterState = 'riesgo'" />
                <span>Riesgo ({{ canalesRiesgo.length }})</span>
              </label>
              <label class="cdm-chip is-critico" :class="{ 'is-active': filterState === 'critico' }">
                <input type="radio" name="filterState" @change="filterState = 'critico'" />
                <span>Criticos ({{ canalesCriticos.length }})</span>
              </label>
            </div>
            <label class="cdm-demo-toggle" title="Mostrar canales demo">
              <input type="checkbox" v-model="showDemos" />
              <span class="cdm-demo-toggle-check"></span>
              <span class="cdm-demo-toggle-label">Ver demos</span>
            </label>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!canalesFiltrados.length" class="cdm-state">
          <div class="cdm-state-icon"><i class="pi pi-info-circle"></i></div>
          <p class="cdm-state-text">{{ searchQuery ? 'Sin resultados para "' + searchQuery + '"' : 'No hay canales con dispositivos conectados' }}</p>
        </div>

        <!-- When a specific state filter is active: flat list (no grouping) -->
        <template v-else-if="filterState !== 'all'">
          <div v-if="viewMode === 'list'" class="cdm-list">
            <div v-for="canal in canalesFiltrados" :key="canal.stream" class="cdm-card cdm-card-channel" :class="healthCardClass(canal)" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
              <div class="cdm-card-body">
                <div class="cdm-channel-bar-left" :class="healthTextClass(canal)"></div>
                <div class="cdm-channel-logo" :style="logoBackgroundStyle(canal)">
                  <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-channel-logo-img" draggable="false" />
                  <i v-else class="pi pi-megaphone cdm-channel-logo-fallback"></i>
                </div>
                <div class="cdm-card-info">
                  <span class="cdm-card-title">{{ canal.radio_name }}</span>
                  <span class="cdm-card-sub cdm-card-sub--health" :class="healthTextClass(canal)">{{ healthLabel(canal) }} &middot; {{ canal.stream }}</span>
                </div>
                <div class="cdm-channel-stats">
                  <div class="cdm-channel-stat-row">
                    <span class="cdm-channel-count" :class="healthTextClass(canal)">{{ canal.conectados.toLocaleString() }}</span>
                    <span class="cdm-channel-sucursales">de {{ canal.sucursales || '?' }}</span>
                  </div>
                  <div class="cdm-channel-bar-track">
                    <div class="cdm-channel-bar-fill" :class="healthBarClass(canal)" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                  </div>
                  <div class="cdm-channel-bar-footer">
                    <span v-if="canal.sucursales" class="cdm-channel-pct" :class="healthTextClass(canal)">{{ canalConexionPct(canal) }}%</span>
                  </div>
                </div>
                <i class="pi pi-chevron-right cdm-card-arrow"></i>
              </div>
            </div>
          </div>
          <div v-else class="cdm-grid">
            <div v-for="canal in canalesFiltrados" :key="'g-' + canal.stream" class="cdm-grid-card" :class="healthCardClass(canal).replace('cdm-card--', 'cdm-grid-card--')" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
              <div class="cdm-grid-card-top">
                <div class="cdm-grid-card-logo" :style="logoBackgroundStyle(canal)">
                  <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-grid-card-logo-img" draggable="false" />
                  <i v-else class="pi pi-megaphone cdm-grid-card-logo-fallback"></i>
                </div>
              </div>
              <div class="cdm-grid-card-body">
                <span class="cdm-grid-card-name">{{ canal.radio_name }}</span>
                <span class="cdm-grid-card-sub cdm-grid-card-sub--health" :class="healthTextClass(canal)">{{ healthLabel(canal) }}</span>
                <span class="cdm-grid-card-count">{{ canal.conectados.toLocaleString() }} de {{ canal.sucursales || '?' }} suc.</span>
                <div class="cdm-grid-card-bar-track">
                  <div class="cdm-grid-card-bar-fill" :class="healthBarClass(canal)" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                </div>
                <span v-if="canal.sucursales" class="cdm-grid-card-pct" :class="healthTextClass(canal)">{{ canalConexionPct(canal) }}%</span>
              </div>
            </div>
          </div>
        </template>

        <!-- When 'Todos' is selected: grouped by health state -->
        <template v-else>
          <!-- Grupo Criticos -->
          <div v-if="canalesGrupoCritico.length" class="cdm-group-section">
            <div class="cdm-group-section-header is-critico" @click="toggleGroup('critico')" style="cursor:pointer">
              <i class="pi pi-exclamation-triangle"></i>
              <span>Criticos</span>
              <span class="cdm-group-section-count">{{ canalesGrupoCritico.length }}</span>
              <i class="pi pi-chevron-down cdm-group-chevron" :class="{ 'is-collapsed': collapsedGroups.critico }"></i>
            </div>
            <transition name="cdm-group-collapse">
            <div v-if="!collapsedGroups.critico">
            <div v-if="viewMode === 'list'" class="cdm-list">
              <div v-for="canal in canalesGrupoCritico" :key="canal.stream" class="cdm-card cdm-card-channel cdm-card--critico" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
                <div class="cdm-card-body">
                  <div class="cdm-channel-bar-left is-red"></div>
                  <div class="cdm-channel-logo" :style="logoBackgroundStyle(canal)">
                    <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-channel-logo-img" draggable="false" />
                    <i v-else class="pi pi-megaphone cdm-channel-logo-fallback"></i>
                  </div>
                  <div class="cdm-card-info">
                    <span class="cdm-card-title">{{ canal.radio_name }}</span>
                    <span class="cdm-card-sub cdm-card-sub--health cdm-card-sub--critico">{{ canalState(canal) }} &middot; {{ canal.stream }}</span>
                  </div>
                  <div class="cdm-channel-stats">
                    <div class="cdm-channel-stat-row">
                      <span class="cdm-channel-count is-red">{{ canal.conectados.toLocaleString() }}</span>
                      <span class="cdm-channel-sucursales">de {{ canal.sucursales || '?' }}</span>
                    </div>
                    <div class="cdm-channel-bar-track">
                      <div class="cdm-channel-bar-fill is-red" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                    </div>
                    <div class="cdm-channel-bar-footer">
                      <span v-if="canal.sucursales" class="cdm-channel-pct is-red">{{ canalConexionPct(canal) }}%</span>
                    </div>
                  </div>
                  <i class="pi pi-chevron-right cdm-card-arrow"></i>
                </div>
              </div>
            </div>
            <div v-else class="cdm-grid">
              <div v-for="canal in canalesGrupoCritico" :key="'g-' + canal.stream" class="cdm-grid-card cdm-grid-card--critico" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
                <div class="cdm-grid-card-top">
                  <div class="cdm-grid-card-logo" :style="logoBackgroundStyle(canal)">
                    <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-grid-card-logo-img" draggable="false" />
                    <i v-else class="pi pi-megaphone cdm-grid-card-logo-fallback"></i>
                  </div>
                </div>
                <div class="cdm-grid-card-body">
                  <span class="cdm-grid-card-name">{{ canal.radio_name }}</span>
                  <span class="cdm-grid-card-sub cdm-grid-card-sub--health is-red">{{ canalState(canal) }}</span>
                  <span class="cdm-grid-card-count">{{ canal.conectados.toLocaleString() }} de {{ canal.sucursales || '?' }} suc.</span>
                  <div class="cdm-grid-card-bar-track">
                    <div class="cdm-grid-card-bar-fill is-red" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                  </div>
                  <span v-if="canal.sucursales" class="cdm-grid-card-pct is-red">{{ canalConexionPct(canal) }}%</span>
                </div>
              </div>
            </div>
            </div>
            </transition>
          </div>

          <!-- Grupo Riesgo -->
          <div v-if="canalesGrupoRiesgo.length" class="cdm-group-section">
            <div class="cdm-group-section-header is-riesgo" @click="toggleGroup('riesgo')" style="cursor:pointer">
              <i class="pi pi-exclamation-circle"></i>
              <span>En riesgo</span>
              <span class="cdm-group-section-count">{{ canalesGrupoRiesgo.length }}</span>
              <i class="pi pi-chevron-down cdm-group-chevron" :class="{ 'is-collapsed': collapsedGroups.riesgo }"></i>
            </div>
            <transition name="cdm-group-collapse">
            <div v-if="!collapsedGroups.riesgo">
            <div v-if="viewMode === 'list'" class="cdm-list">
              <div v-for="canal in canalesGrupoRiesgo" :key="canal.stream" class="cdm-card cdm-card-channel cdm-card--riesgo" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
                <div class="cdm-card-body">
                  <div class="cdm-channel-bar-left is-yellow"></div>
                  <div class="cdm-channel-logo" :style="logoBackgroundStyle(canal)">
                    <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-channel-logo-img" draggable="false" />
                    <i v-else class="pi pi-megaphone cdm-channel-logo-fallback"></i>
                  </div>
                  <div class="cdm-card-info">
                    <span class="cdm-card-title">{{ canal.radio_name }}</span>
                    <span class="cdm-card-sub cdm-card-sub--health cdm-card-sub--riesgo">{{ canalState(canal) }} &middot; {{ canal.stream }}</span>
                  </div>
                  <div class="cdm-channel-stats">
                    <div class="cdm-channel-stat-row">
                      <span class="cdm-channel-count is-yellow">{{ canal.conectados.toLocaleString() }}</span>
                      <span class="cdm-channel-sucursales">de {{ canal.sucursales || '?' }}</span>
                    </div>
                    <div class="cdm-channel-bar-track">
                      <div class="cdm-channel-bar-fill is-yellow" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                    </div>
                    <div class="cdm-channel-bar-footer">
                      <span v-if="canal.sucursales" class="cdm-channel-pct is-yellow">{{ canalConexionPct(canal) }}%</span>
                    </div>
                  </div>
                  <i class="pi pi-chevron-right cdm-card-arrow"></i>
                </div>
              </div>
            </div>
            <div v-else class="cdm-grid">
              <div v-for="canal in canalesGrupoRiesgo" :key="'g-' + canal.stream" class="cdm-grid-card cdm-grid-card--riesgo" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
                <div class="cdm-grid-card-top">
                  <div class="cdm-grid-card-logo" :style="logoBackgroundStyle(canal)">
                    <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-grid-card-logo-img" draggable="false" />
                    <i v-else class="pi pi-megaphone cdm-grid-card-logo-fallback"></i>
                  </div>
                </div>
                <div class="cdm-grid-card-body">
                  <span class="cdm-grid-card-name">{{ canal.radio_name }}</span>
                  <span class="cdm-grid-card-sub cdm-grid-card-sub--health is-yellow">{{ canalState(canal) }}</span>
                  <span class="cdm-grid-card-count">{{ canal.conectados.toLocaleString() }} de {{ canal.sucursales || '?' }} suc.</span>
                  <div class="cdm-grid-card-bar-track">
                    <div class="cdm-grid-card-bar-fill is-yellow" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                  </div>
                  <span v-if="canal.sucursales" class="cdm-grid-card-pct is-yellow">{{ canalConexionPct(canal) }}%</span>
                </div>
              </div>
            </div>
            </div>
            </transition>
          </div>

          <!-- Grupo Optimos -->
          <div v-if="canalesGrupoOptimo.length" class="cdm-group-section">
            <div class="cdm-group-section-header is-optimo" @click="toggleGroup('optimo')" style="cursor:pointer">
              <i class="pi pi-check-circle"></i>
              <span>Optimos</span>
              <span class="cdm-group-section-count">{{ canalesGrupoOptimo.length }}</span>
              <i class="pi pi-chevron-down cdm-group-chevron" :class="{ 'is-collapsed': collapsedGroups.optimo }"></i>
            </div>
            <transition name="cdm-group-collapse">
            <div v-if="!collapsedGroups.optimo">
            <div v-if="viewMode === 'list'" class="cdm-list">
              <div v-for="canal in canalesGrupoOptimo" :key="canal.stream" class="cdm-card cdm-card-channel cdm-card--optimo" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
                <div class="cdm-card-body">
                  <div class="cdm-channel-bar-left is-green"></div>
                  <div class="cdm-channel-logo" :style="logoBackgroundStyle(canal)">
                    <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-channel-logo-img" draggable="false" />
                    <i v-else class="pi pi-megaphone cdm-channel-logo-fallback"></i>
                  </div>
                  <div class="cdm-card-info">
                    <span class="cdm-card-title">{{ canal.radio_name }}</span>
                    <span class="cdm-card-sub cdm-card-sub--health cdm-card-sub--optimo">{{ canalState(canal) }} &middot; {{ canal.stream }}</span>
                  </div>
                  <div class="cdm-channel-stats">
                    <div class="cdm-channel-stat-row">
                      <span class="cdm-channel-count is-green">{{ canal.conectados.toLocaleString() }}</span>
                      <span class="cdm-channel-sucursales">de {{ canal.sucursales || '?' }}</span>
                    </div>
                    <div class="cdm-channel-bar-track">
                      <div class="cdm-channel-bar-fill is-green" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                    </div>
                    <div class="cdm-channel-bar-footer">
                      <span v-if="canal.sucursales" class="cdm-channel-pct is-green">{{ canalConexionPct(canal) }}%</span>
                    </div>
                  </div>
                  <i class="pi pi-chevron-right cdm-card-arrow"></i>
                </div>
              </div>
            </div>
            <div v-else class="cdm-grid">
              <div v-for="canal in canalesGrupoOptimo" :key="'g-' + canal.stream" class="cdm-grid-card cdm-grid-card--optimo" @click="cargarDetalle(canal.stream, canal.radio_name, canal.sucursales)">
                <div class="cdm-grid-card-top">
                  <div class="cdm-grid-card-logo" :style="logoBackgroundStyle(canal)">
                    <img v-if="getLogoUrl(canal)" :src="getLogoUrl(canal)" :alt="canal.radio_name || ''" class="cdm-grid-card-logo-img" draggable="false" />
                    <i v-else class="pi pi-megaphone cdm-grid-card-logo-fallback"></i>
                  </div>
                </div>
                <div class="cdm-grid-card-body">
                  <span class="cdm-grid-card-name">{{ canal.radio_name }}</span>
                  <span class="cdm-grid-card-sub cdm-grid-card-sub--health is-green">{{ canalState(canal) }}</span>
                  <span class="cdm-grid-card-count">{{ canal.conectados.toLocaleString() }} de {{ canal.sucursales || '?' }} suc.</span>
                  <div class="cdm-grid-card-bar-track">
                    <div class="cdm-grid-card-bar-fill is-green" :style="{ width: canalConexionPct(canal) + '%' }"></div>
                  </div>
                  <span v-if="canal.sucursales" class="cdm-grid-card-pct is-green">{{ canalConexionPct(canal) }}%</span>
                </div>
              </div>
            </div>
            </div>
            </transition>
          </div>
        </template>
      </div>

      <!-- ── View 2: Dispositivos ── -->
      <div v-else-if="selectedStream && !selectedDevice" key="dispositivos">
        <div class="cdm-hero cdm-hero--sm">
          <div class="cdm-hero-label">Dispositivos en <strong>{{ radioName || selectedStream }}</strong></div>
          <div class="cdm-hero-value">
            {{ metricOnline }}<span class="cdm-hero-unit">online de
              <template v-if="streamSucursales != null && streamSucursales > 0">
                {{ streamSucursales }} sucursales
              </template>
              <template v-else>
                {{ dispositivos.length }} dispositivos
              </template>
            </span>
          </div>
          <div v-if="streamSucursales != null && streamSucursales > 0" class="cdm-hero-bar">
            <div class="cdm-hero-bar-track">
              <div class="cdm-hero-bar-fill" :class="conexionPctStream >= 90 ? '' : conexionPctStream >= 50 ? 'is-yellow' : 'is-red'" :style="{ width: Math.max(4, conexionPctStream) + '%' }"></div>
            </div>
            <span class="cdm-hero-bar-pct" :class="conexionPctStream >= 90 ? '' : conexionPctStream >= 50 ? 'is-yellow' : 'is-red'">{{ conexionPctStream }}% conexión</span>
          </div>
        </div>
        <div class="cdm-toolbar">
          <div class="cdm-search">
            <i class="pi pi-search cdm-search-icon"></i>
            <input v-model="deviceSearch" type="text" class="cdm-search-input" placeholder="Buscar dispositivo…" />
          </div>
          <div class="cdm-chip-group" role="group" aria-label="Filtrar dispositivos">
            <label class="cdm-chip" :class="{ 'is-active': deviceFilter === 'all' }">
              <input type="radio" name="deviceFilter" checked="deviceFilter === 'all'" @change="deviceFilter = 'all'" />
              <span>Todos</span>
            </label>
            <label class="cdm-chip" :class="{ 'is-active': deviceFilter === 'playing' }">
              <input type="radio" name="deviceFilter" @change="deviceFilter = 'playing'" />
              <span>▶ Reproduciendo</span>
            </label>
            <label class="cdm-chip is-paused" :class="{ 'is-active': deviceFilter === 'paused' }">
              <input type="radio" name="deviceFilter" @change="deviceFilter = 'paused'" />
              <span>⏸ En pausa</span>
            </label>
            <label class="cdm-chip is-unidentified" :class="{ 'is-active': deviceFilter === 'unidentified' }">
              <input type="radio" name="deviceFilter" @change="deviceFilter = 'unidentified'" />
              <span>Sin Identificar</span>
            </label>
          </div>
        </div>
        <div v-if="dispositivosFiltrados.length === 0" class="cdm-state">
          <div class="cdm-state-icon"><i class="pi pi-info-circle"></i></div>
          <p class="cdm-state-text">No hay dispositivos que coincidan con los filtros</p>
        </div>
        <div v-else class="cdm-list">
          <template v-for="(grupo, gIdx) in dispositivosAgrupados" :key="gIdx">
            <div class="cdm-group-header" :class="grupo.groupPriority <= 1 ? 'is-critico' : grupo.groupPriority === 2 ? 'is-riesgo' : 'is-optimo'">
              <i class="pi pi-map-marker"></i>
              <span>{{ grupo.identifier }}</span>
              <span v-if="grupo.identifier === 'Sin Identificar'" class="cdm-group-detail">— por asignar ID/VAR</span>
              <span class="cdm-group-count">{{ grupo.devices.length }} disp.</span>
            </div>
            <div v-for="dev in grupo.devices" :key="dev.device_id" class="cdm-card cdm-card--device" :class="{ 'is-offline': !dev.online, 'is-unidentified': dev.unidentified, 'is-paused': dev.isPaused }" @click="cargarSesiones(dev.device_id)">
              <div class="cdm-card-body">
                <div class="cdm-device-status-col">
                  <div class="cdm-online-dot" :class="dev.online ? 'is-online' : 'is-offline'" :title="dev.online ? 'En línea' : 'Desconectado'"></div>
                  <div v-if="dev.deviceHealth !== null" class="cdm-health-indicator" :class="healthScoreClass(dev.deviceHealth)" :title="'Health: ' + dev.deviceHealth + '%'">
                    <div class="cdm-health-fill" :style="{ height: dev.deviceHealth + '%' }"></div>
                  </div>
                </div>
                <div class="cdm-card-info">
                  <div class="cdm-device-primary-row">
                    <span class="cdm-device-name">
                      {{ displayDeviceTitle(dev) }}
                    </span>
                    <span v-if="dev.fuente === 'legacy'" class="cdm-badge cdm-badge--amber">legacy</span>
                    <span v-if="dev.unidentified" class="cdm-badge cdm-badge--unidentified">Sin identificar</span>
                  </div>
                  <div class="cdm-device-secondary-row">
                    <template v-if="dev.unidentified">
                      <span class="cdm-missing-fields">ID:{{ (dev.suc ?? 0) || '—' }} VAR:{{ (dev.var ?? 0) || '—' }} CAT:{{ (dev.cat ?? 0) || '—' }}</span>
                      <span class="cdm-detail-sep">·</span>
                    </template>
                    <span v-if="dev.playing === true" class="cdm-device-status-badge is-playing">▶ Reproduciendo</span>
                    <span v-else-if="dev.playing === false" class="cdm-device-status-badge is-paused">⏸ En pausa</span>
                    <span v-else class="cdm-device-status-badge is-unknown">—</span>
                    <span class="cdm-detail-sep">·</span>
                    <span v-if="dev.version" class="cdm-device-version">v{{ dev.version }}</span>
                    <template v-if="dev.categoria_nombre">
                      <span class="cdm-detail-sep">·</span>
                      <span class="cdm-device-cat">{{ dev.categoria_nombre }}</span>
                    </template>
                    <template v-if="dev.audio_name">
                      <span class="cdm-detail-sep">·</span>
                      <span class="cdm-device-audio">♪ {{ dev.audio_name }}</span>
                    </template>
                  </div>
                  <div class="cdm-device-meta-row">
                    <span class="cdm-time-ago" :class="{ 'is-offline': !dev.online }" :title="formatDateTime(dev.ultimo_heartbeat)">
                      <i class="pi pi-clock cdm-meta-icon"></i>{{ timeAgo(dev.ultimo_heartbeat) }}
                    </span>
                    <span v-if="!dev.online" class="cdm-offline-label">Desconectado</span>
                    <span v-if="dev.unidentified" class="cdm-missing-fields-meta">sin identificar</span>
                    <span v-if="dev.device_id" class="cdm-device-id-tiny" :title="'Device ID: ' + dev.device_id">{{ displayDeviceId(dev.device_id) }}</span>
                  </div>
                </div>
                <div class="cdm-device-right-col">
                  <span v-if="dev.deviceHealth !== null" class="cdm-device-health-num" :class="healthScoreClass(dev.deviceHealth)">{{ dev.deviceHealth }}%</span>
                  <i class="pi pi-chevron-right cdm-card-arrow"></i>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- ── View 3: Dashboard del dispositivo ── -->
      <div v-else-if="selectedDevice" key="timeline">

        <!-- Sin datos -->
        <div v-if="!selectedDeviceData" class="cdm-state">
          <div class="cdm-state-icon"><i class="pi pi-info-circle"></i></div>
          <p class="cdm-state-text">No hay datos disponibles para este dispositivo hoy</p>
          <div class="cdm-state-actions">
            <button class="cdm-state-btn" @click="recargarVistaActual"><i class="pi pi-refresh mr-1"></i>Reintentar</button>
          </div>
        </div>

        <template v-else>
          <!-- ── Card principal: identidad + estado ── -->
          <div class="cdm-device-hero" :class="{ 'is-offline': !deviceOnline }">
            <div class="cdm-device-hero-status-row">
              <div class="cdm-online-dot" :class="deviceOnline ? 'is-online' : 'is-offline'" :title="deviceOnline ? 'En línea' : 'Desconectado'"></div>
              <span class="cdm-device-hero-status-label" :class="{ 'is-offline': !deviceOnline }">{{ deviceOnline ? 'En línea' : 'Desconectado' }}</span>
              <span v-if="!deviceOnline && offlineMinutes !== null" class="cdm-hero-offline-time">— {{ offlineMinutes }}min sin señal</span>
              <template v-if="deviceOnline">
                <span class="cdm-detail-sep">·</span>
                <span class="cdm-device-hero-playing" :class="{ 'is-active': selectedDeviceData?.playing }">
                  {{ selectedDeviceData?.playing ? '▶ Reproduciendo' : '⏸ En pausa' }}
                </span>
              </template>
            </div>
            <h3 class="cdm-device-hero-title">
              {{ displayDeviceTitle(selectedDeviceData) }}
              <!-- ── Label badge + admin actions ── -->
              <template v-if="!editingLabel">
                <span v-if="getDeviceLabel(selectedDevice)" class="cdm-device-label-badge">
                  <i class="pi pi-tag"></i>
                  {{ getDeviceLabel(selectedDevice) }}
                </span>
                <template v-if="isAdmin">
                  <button
                    v-if="getDeviceLabel(selectedDevice)"
                    class="cdm-label-btn cdm-label-btn--edit"
                    @click.stop="toggleLabelEditor"
                    title="Editar etiqueta"
                    :disabled="savingLabel"
                  >
                    <i class="pi pi-pencil"></i>
                  </button>
                  <button
                    v-if="getDeviceLabel(selectedDevice)"
                    class="cdm-label-btn cdm-label-btn--delete"
                    @click.stop="deleteDeviceLabel"
                    title="Eliminar etiqueta"
                    :disabled="savingLabel"
                  >
                    <i class="pi pi-trash"></i>
                  </button>
                  <button
                    v-if="!getDeviceLabel(selectedDevice)"
                    class="cdm-label-btn cdm-label-btn--add"
                    @click.stop="toggleLabelEditor"
                    title="Asignar etiqueta"
                    :disabled="savingLabel"
                  >
                    <i class="pi pi-tag"></i>+ Etiqueta
                  </button>
                </template>
              </template>
              <!-- ── Label editor inline ── -->
              <template v-else>
                <span class="cdm-label-editor">
                  <input
                    v-model="labelInput"
                    type="text"
                    class="cdm-label-input"
                    :class="{ 'has-error': labelError }"
                    placeholder="Nombre de la etiqueta…"
                    maxlength="255"
                    ref="labelInputRef"
                    @keyup.enter="saveDeviceLabel"
                    @keyup.esc="cancelLabelEdit"
                  />
                  <button class="cdm-label-btn cdm-label-btn--save" @click.stop="saveDeviceLabel" :disabled="savingLabel || !labelInput.trim()">
                    <i class="pi pi-check"></i>
                  </button>
                  <button class="cdm-label-btn cdm-label-btn--cancel" @click.stop="cancelLabelEdit" :disabled="savingLabel">
                    <i class="pi pi-times"></i>
                  </button>
                </span>
              </template>
            </h3>
            <span v-if="labelError" class="cdm-label-error">{{ labelError }}</span>
            <div class="cdm-device-hero-meta">
              <span class="cdm-hero-meta-item">
                <i class="pi pi-megaphone cdm-meta-icon-sm"></i>
                {{ selectedDeviceData?.radio_name || selectedDeviceData?.stream || '—' }}
              </span>
              <span v-if="selectedDeviceData?.version" class="cdm-detail-sep">·</span>
              <span v-if="selectedDeviceData?.version" class="cdm-hero-meta-item">
                <i class="pi pi-cog cdm-meta-icon-sm"></i>
                v{{ selectedDeviceData.version }}
              </span>
              <span v-if="selectedDeviceData?.ip" class="cdm-detail-sep">·</span>
              <span v-if="selectedDeviceData?.ip" class="cdm-hero-meta-item cdm-hero-ip">{{ selectedDeviceData.ip }}</span>
              <span class="cdm-detail-sep">·</span>
              <span class="cdm-hero-meta-item cdm-hero-device-id" :title="selectedDevice">{{ displayDeviceId(selectedDevice) }}</span>
            </div>

            <!-- ── Metricas principales (3 columnas) ── -->
            <div class="cdm-device-metrics cdm-device-metrics--3col">
              <div class="cdm-device-metric">
                <span class="cdm-device-metric-val">{{ selectedDeviceData.heartbeats }}</span>
                <span class="cdm-device-metric-lbl">Señales hoy</span>
              </div>
              <div class="cdm-device-metric">
                <span class="cdm-device-metric-val cdm-device-metric-val--muted">{{ formatDuracion(selectedDeviceData.duracion_minutos || 0) }}</span>
                <span class="cdm-device-metric-lbl">Tiempo activo</span>
              </div>
              <div class="cdm-device-metric">
                <span class="cdm-device-metric-val" :class="deviceHealthClass">
                  {{ selectedDeviceData.health_score !== null ? selectedDeviceData.health_score + '%' : '—' }}
                </span>
                <span class="cdm-device-metric-lbl">Confiabilidad</span>
              </div>
            </div>

            <!-- ── Health bar (sutil, solo cuando hay duracion real) ── -->
            <div v-if="selectedDeviceData.health_score !== null && selectedDeviceData.duracion_minutos > 2" class="cdm-device-health-bar-wrapper">
              <div class="cdm-device-health-bar-track" :title="deviceHealthTooltip">
                <div class="cdm-device-health-bar-fill" :class="deviceHealthClass" :style="{ width: Math.min(selectedDeviceData.health_score, 100) + '%' }"></div>
              </div>
              <span class="cdm-device-health-bar-label">
                <strong>{{ selectedDeviceData.heartbeats }}</strong> de {{ selectedDeviceData.heartbeats_esperados || '?' }} señales esperadas
              </span>
            </div>
          </div>

          <!-- ── Audio actual ── -->
          <div v-if="selectedDeviceData.audio_name" class="cdm-device-audio-card">
            <div class="cdm-device-audio-icon">
              <i class="pi pi-volume-up" :class="{ 'is-playing pulse': selectedDeviceData.playing }"></i>
            </div>
            <div class="cdm-device-audio-info">
              <span class="cdm-device-audio-title">{{ selectedDeviceData.audio_name }}</span>
              <span class="cdm-device-audio-meta">
                <i class="pi pi-building"></i> {{ selectedDeviceData.radio_name || selectedDeviceData.stream }}
                <span v-if="selectedDeviceData.variante_nombre" class="cdm-detail-sep">·</span>
                <span v-if="selectedDeviceData.variante_nombre">{{ selectedDeviceData.variante_nombre }}</span>
              </span>
            </div>
          </div>

          <!-- ── Detalles (solo info complementaria, sin repetir hero) ── -->
          <div class="cdm-device-details">
            <div class="cdm-device-details-grid">
              <div v-if="selectedDeviceData?.categoria_nombre" class="cdm-device-detail-item">
                <span class="cdm-device-detail-label">Categoría</span>
                <span class="cdm-device-detail-value">{{ selectedDeviceData.categoria_nombre }}</span>
              </div>
              <div v-if="selectedDeviceData?.variante_nombre && selectedDeviceData?.variante_nombre !== selectedDeviceData?.sucursal_nombre" class="cdm-device-detail-item">
                <span class="cdm-device-detail-label">Variante</span>
                <span class="cdm-device-detail-value">{{ selectedDeviceData.variante_nombre }}</span>
              </div>
              <div class="cdm-device-detail-item">
                <span class="cdm-device-detail-label">Última señal</span>
                <span class="cdm-device-detail-value">{{ formatDateTime(selectedDeviceData?.ultimo_heartbeat) }}</span>
              </div>
              <div v-if="selectedDeviceData?.session_id" class="cdm-device-detail-item">
                <span class="cdm-device-detail-label">Sesión (ID completo)</span>
                <span class="cdm-device-detail-value cdm-device-detail-value--mono cdm-device-detail-value--break">{{ selectedDeviceData.session_id }}</span>
              </div>

              <!-- ── Fingerprint ── -->
              <template v-if="deviceFingerprint">
                <div class="cdm-device-detail-item">
                  <span class="cdm-device-detail-label">Navegador</span>
                  <span class="cdm-device-detail-value">
                    <template v-if="getParsedUserAgent(deviceFingerprint.user_agent)">
                      {{ getParsedUserAgent(deviceFingerprint.user_agent).browser }}<br>
                      <span style="font-size:0.68rem;opacity:0.6">{{ getParsedUserAgent(deviceFingerprint.user_agent).os }}</span>
                    </template>
                    <template v-else>—</template>
                  </span>
                </div>
                <div v-if="deviceFingerprint.screen_resolution" class="cdm-device-detail-item">
                  <span class="cdm-device-detail-label">Pantalla</span>
                  <span class="cdm-device-detail-value">{{ deviceFingerprint.screen_resolution }}<span v-if="deviceFingerprint.device_pixel_ratio > 0"> @{{ deviceFingerprint.device_pixel_ratio }}x</span></span>
                </div>
                <div v-if="deviceFingerprint.platform" class="cdm-device-detail-item">
                  <span class="cdm-device-detail-label">Plataforma</span>
                  <span class="cdm-device-detail-value">{{ deviceFingerprint.platform }}</span>
                </div>
                <div class="cdm-device-detail-item">
                  <span class="cdm-device-detail-label">Hardware</span>
                  <span class="cdm-device-detail-value">
                    <template v-if="deviceFingerprint.cpu_cores > 0 || deviceFingerprint.device_memory > 0">
                      {{ deviceFingerprint.cpu_cores > 0 ? deviceFingerprint.cpu_cores + ' núcleos' : '' }}
                      {{ deviceFingerprint.cpu_cores > 0 && deviceFingerprint.device_memory > 0 ? ' · ' : '' }}
                      {{ deviceFingerprint.device_memory > 0 ? deviceFingerprint.device_memory + 'GB RAM' : '' }}
                    </template>
                    <template v-else>—</template>
                  </span>
                </div>
                <div v-if="deviceFingerprint.timezone" class="cdm-device-detail-item" style="grid-column: span 2">
                  <span class="cdm-device-detail-label">Zona horaria</span>
                  <span class="cdm-device-detail-value">{{ deviceFingerprint.timezone }}</span>
                </div>
              </template>
            </div>
          </div>

          <!-- ── Confiabilidad de conexión ── -->
          <div v-if="offlineData" class="cdm-reliability">
            <div class="cdm-reliability-header">
              <i class="pi pi-bolt cdm-reliability-icon"></i>
              <span>Confiabilidad de conexión</span>
            </div>
            <div class="cdm-reliability-summary">
              <div class="cdm-reliability-stat">
                <span class="cdm-reliability-stat-val" :class="offlineData.offline_events > 0 ? 'is-warn' : 'is-good'">{{ offlineData.offline_events }}</span>
                <span class="cdm-reliability-stat-lbl">Desconexiones hoy</span>
              </div>
              <div class="cdm-reliability-stat-divider"></div>
              <div class="cdm-reliability-stat">
                <span class="cdm-reliability-stat-val" :class="offlineData.minutos_offline_total > 10 ? 'is-warn' : offlineData.minutos_offline_total > 0 ? 'is-mid' : 'is-good'">{{ offlineData.minutos_offline_total }}min</span>
                <span class="cdm-reliability-stat-lbl">Tiempo offline total</span>
              </div>
            </div>
            <div v-if="offlineData.historial && offlineData.historial.length > 0" class="cdm-reliability-history">
              <span class="cdm-reliability-history-title">Historial de desconexiones</span>
              <div v-for="(ev, i) in offlineData.historial" :key="i" class="cdm-reliability-row" :class="{ 'is-ongoing': ev.abierto }" :title="ev.fin ? ('Del ' + formatDateTime(ev.inicio) + ' al ' + formatDateTime(ev.fin)) : ('Desde ' + formatDateTime(ev.inicio) + ' — en curso')">
                <div class="cdm-reliability-row-time">
                  <span class="cdm-reliability-row-time-val">{{ formatTimeCompact(ev.inicio) }}</span>
                  <span class="cdm-reliability-row-time-arrow">→</span>
                  <span v-if="ev.fin" class="cdm-reliability-row-time-val">{{ formatTimeCompact(ev.fin) }}</span>
                  <span v-else class="cdm-reliability-row-time-val is-ongoing">en curso</span>
                </div>
                <div class="cdm-reliability-row-bar-wrapper">
                  <div class="cdm-reliability-row-bar" :style="{ width: offlineBarWidth(ev.duracion_minutos) }"></div>
                </div>
                <span class="cdm-reliability-row-dur">{{ formatDuracion(ev.duracion_minutos) }}</span>
              </div>
            </div>
            <div v-else class="cdm-reliability-note">
              <i class="pi pi-check-circle cdm-reliability-note-icon"></i>
              <span>Sin desconexiones detectadas hoy</span>
            </div>
          </div>

        </template>
      </div>

    </transition>
    </div>
  </Dialog>
</template>

<script>
import axiosInstance from '@/axiosInstance';

const CINCO_MIN = 5 * 60 * 1000;

export default {
  name: 'ConectadosDetalleModal',

  props: {
    visible: { type: Boolean, default: false },
    conectadosAhora: { type: Object, default: null },
    totalSucursalesKpi: { type: Number, default: null },
    initialStream: { type: String, default: null },
    initialRadioName: { type: String, default: null },
    initialSucursales: { type: Number, default: null },
  },

  emits: ['update:visible', 'update:initialStream', 'close'],

  data() {
    return {
      dialogVisible: false,
      loading: false,
      error: null,
      selectedStream: null,
      radioName: null,
      dispositivos: [],
      streamSucursales: null,
      selectedDevice: null,
      selectedDeviceData: null,
      offlineData: null,
      deviceLabels: {},
      deviceFingerprint: null,
      editingLabel: false,
      labelInput: '',
      savingLabel: false,
      labelError: '',
      viewMode: 'list',
      filterState: 'all',
      collapsedGroups: { critico: false, riesgo: false, optimo: false },
      searchQuery: '',
      deviceSearch: '',
      deviceFilter: 'all',
      now: Date.now(),
      refreshTimer: null,
      timeInterval: null,
      isRefreshing: false,
      scrollPositions: {},
      autoRefreshEnabled: true,
      showDemos: false,
    };
  },

  computed: {
    viewKey() {
      if (this.loading) return 'loading';
      if (this.error) return 'error';
      if (!this.selectedStream) return 'sucursales';
      if (!this.selectedDevice) return 'dispositivos';
      return 'timeline';
    },

    deviceOnline() {
      if (!this.selectedDeviceData?.ultimo_heartbeat) return false;
      const hb = new Date(this.selectedDeviceData.ultimo_heartbeat).getTime();
      return !Number.isNaN(hb) && (Date.now() - hb) < CINCO_MIN;
    },

    offlineMinutes() {
      if (!this.selectedDeviceData?.ultimo_heartbeat) return null;
      const hb = new Date(this.selectedDeviceData.ultimo_heartbeat).getTime();
      if (Number.isNaN(hb)) return null;
      return Math.floor((Date.now() - hb) / 60000);
    },

    deviceHealthClass() {
      const s = this.selectedDeviceData?.health_score;
      if (s === null || s === undefined) return '';
      if (s >= 80) return 'is-green';
      if (s >= 50) return 'is-yellow';
      return 'is-red';
    },

    deviceHealthTooltip() {
      const d = this.selectedDeviceData;
      if (!d || d.health_score === null) return '';
      return `Confiabilidad ${d.health_score}%: ${d.heartbeats} de ${d.heartbeats_esperados || '?'} señales esperadas`;
    },

    /** Sucursales ordenadas por conectados descendente */
    canalesSorted() {
      const c = this.conectadosAhora?.canales;
      if (!Array.isArray(c) || c.length === 0) return [];
      return [...c].sort((a, b) => (b.conectados || 0) - (a.conectados || 0));
    },

    /** canales filtrados por búsqueda (usado por todas las vistas) */
    canalesPorBusqueda() {
      let c = this.canalesSorted;
      if (!this.showDemos) {
        const excluded = ['intercambio', 'cortesia', 'cortesía', 'demo', 'demos', 'pruebas'];
        c = c.filter(ch => {
          const t = (ch.radio_type || '').toLowerCase().trim();
          return !excluded.includes(t);
        });
      }
      if (!this.searchQuery) return c;
      const q = this.searchQuery.toLowerCase();
      return c.filter(
        ch => ch.radio_name?.toLowerCase().includes(q) || ch.stream?.toLowerCase().includes(q)
      );
    },

    canalesOptimos() {
      return this.canalesPorBusqueda.filter(c => this.canalState(c) === 'optimo');
    },

    canalesRiesgo() {
      return this.canalesPorBusqueda.filter(c => this.canalState(c) === 'riesgo');
    },

    canalesCriticos() {
      return this.canalesPorBusqueda.filter(c => this.canalState(c) === 'critico');
    },

    /** Canal con la conexion mas baja */
    worstCanal() {
      if (!this.canalesCriticos.length) return null;
      let worst = null;
      let worstPct = 101;
      for (const c of this.canalesCriticos) {
        const pct = this.canalConexionPct(c);
        if (pct < worstPct) { worstPct = pct; worst = c; }
      }
      return worst;
    },

    canalesGrupoCritico() {
      return [...this.canalesCriticos].sort((a, b) => this.canalConexionPct(a) - this.canalConexionPct(b));
    },

    canalesGrupoRiesgo() {
      return [...this.canalesRiesgo].sort((a, b) => this.canalConexionPct(a) - this.canalConexionPct(b));
    },

    canalesGrupoOptimo() {
      return [...this.canalesOptimos].sort((a, b) => this.canalConexionPct(b) - this.canalConexionPct(a));
    },

    canalesFiltrados() {
      let list = this.canalesPorBusqueda;
      if (this.filterState === 'optimo') list = this.canalesOptimos;
      else if (this.filterState === 'riesgo') list = this.canalesRiesgo;
      else if (this.filterState === 'critico') list = this.canalesCriticos;
      return [...list].sort((a, b) => (b.conectados || 0) - (a.conectados || 0));
    },

    dispositivosFiltrados() {
      if (!Array.isArray(this.dispositivos) || this.dispositivos.length === 0) return [];
      const AHORA = Date.now();
      let list = this.dispositivos.map((d) => {
        let online = false;
        if (d.ultimo_heartbeat) {
          const hb = new Date(d.ultimo_heartbeat).getTime();
          online = !Number.isNaN(hb) && (AHORA - hb) < CINCO_MIN;
        }
        let deviceHealth = d.health_score ?? null;
        if (deviceHealth === null && d.heartbeats_hoy && d.duracion_minutos && d.duracion_minutos > 1) {
          const esp = Math.max(1, Math.ceil(d.duracion_minutos / 2.5));
          deviceHealth = Math.min(100, Math.round(d.heartbeats_hoy / esp * 100));
        }
        const unidentified = !d.sucursal_nombre && !d.variante_nombre;
        const isPaused = d.playing === false;
        let statusPriority = 3;
        if (!online) statusPriority = 0;
        else if (unidentified) statusPriority = 1;
        else if (isPaused) statusPriority = 2;
        return { ...d, online, deviceHealth, unidentified, isPaused, statusPriority };
      });

      if (this.deviceFilter === 'playing') list = list.filter(d => d.playing === true);
      else if (this.deviceFilter === 'paused') list = list.filter(d => d.playing === false);
      else if (this.deviceFilter === 'unidentified') list = list.filter(d => d.unidentified);

      if (this.deviceSearch) {
        const q = this.deviceSearch.toLowerCase();
        list = list.filter(d =>
          d.device_id?.toLowerCase().includes(q) ||
          d.sucursal_nombre?.toLowerCase().includes(q) ||
          d.ip?.includes(q) ||
          d.categoria_nombre?.toLowerCase().includes(q)
        );
      }

      return list.sort((a, b) => {
        if (a.statusPriority !== b.statusPriority) return a.statusPriority - b.statusPriority;
        const aTs = a.ultimo_heartbeat ? new Date(a.ultimo_heartbeat).getTime() : 0;
        const bTs = b.ultimo_heartbeat ? new Date(b.ultimo_heartbeat).getTime() : 0;
        return (bTs || 0) - (aTs || 0);
      });
    },

    dispositivosAgrupados() {
      const grupos = {};
      for (const dev of this.dispositivosFiltrados) {
        const key = dev.sucursal_nombre || dev.variante_nombre || 'Sin Identificar';
        if (!grupos[key]) grupos[key] = { identifier: key, devices: [], groupPriority: 3 };
        grupos[key].devices.push(dev);
        if (dev.statusPriority < grupos[key].groupPriority) {
          grupos[key].groupPriority = dev.statusPriority;
        }
      }
      return Object.values(grupos).sort((a, b) => {
        if (a.groupPriority !== b.groupPriority) return a.groupPriority - b.groupPriority;
        if (a.identifier === 'Sin Identificar') return 1;
        if (b.identifier === 'Sin Identificar') return -1;
        return b.devices.length - a.devices.length;
      });
    },

    modalWidth() {
      if (this.selectedDevice) return '680px';
      if (this.selectedStream) return '820px';
      return '640px';
    },

    heroTotalSucursales() {
      return this.totalSucursalesKpi ?? this.conectadosAhora?.total_sucursales ?? 0;
    },

    conexionPctGlobal() {
      const total = this.conectadosAhora?.total || 0;
      const suc = this.heroTotalSucursales;
      if (!suc) return 0;
      return Math.min(100, Math.round((total / suc) * 100));
    },

    conexionPctStream() {
      const suc = this.streamSucursales;
      if (suc == null || suc <= 0 || !this.metricOnline) return 0;
      return Math.min(100, Math.round((this.metricOnline / suc) * 100));
    },

    metricOnline() {
      if (!Array.isArray(this.dispositivos)) return 0;
      return this.dispositivos.filter(d => {
        if (!d.ultimo_heartbeat) return false;
        const hb = new Date(d.ultimo_heartbeat).getTime();
        return !Number.isNaN(hb) && (Date.now() - hb) < CINCO_MIN;
      }).length;
    },

    recargarLabel() {
      if (this.loading) return 'Cargando…';
      if (this.selectedDevice) return 'Recargar dispositivo';
      if (this.selectedStream) return 'Recargar dispositivos';
      return 'Recargar';
    },

    isAdmin() {
      try {
        const data = JSON.parse(localStorage.getItem('clientData') || '{}');
        return Array.isArray(data.permissions) && data.permissions.includes('admin');
      } catch (e) {
        return false;
      }
    },
  },

  beforeUnmount() {
    this.detenerAutoRefresh();
  },

  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          if (this.initialStream) {
            this.cargarDetalle(this.initialStream, this.initialRadioName, this.initialSucursales);
            this.$emit('update:initialStream', null);
          } else {
            this.irARaices();
          }
          this.iniciarAutoRefresh();
          this.cargarDeviceLabels();
          this.cargarDeviceFingerprint();
        } else {
          this.detenerAutoRefresh();
        }
      },
      immediate: true,
    },
  },

  methods: {
    /* ── Device fingerprint ── */
    async cargarDeviceFingerprint() {
      this.deviceFingerprint = null;
      if (!this.selectedDevice) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axiosInstance.get('/api/device_fingerprint_get', {
          params: { device_id: this.selectedDevice, token },
        });
        const data = res.data?.data || res.data;
        if (data?.fingerprint) {
          this.deviceFingerprint = data.fingerprint;
        }
      } catch (e) {
        // Silently fail — fingerprint is optional
        this.deviceFingerprint = null;
      }
    },

    getParsedUserAgent(ua) {
      if (!ua) return null;
      // Extraer info básica del user agent
      let browser = 'Desconocido';
      let os = '';
      if (ua.includes('Chrome/')) {
        const m = ua.match(/Chrome\/([\d.]+)/);
        browser = 'Chrome ' + (m ? m[1].split('.')[0] : '');
      } else if (ua.includes('Firefox/')) {
        const m = ua.match(/Firefox\/([\d.]+)/);
        browser = 'Firefox ' + (m ? m[1].split('.')[0] : '');
      } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
        const m = ua.match(/Version\/([\d.]+)/);
        browser = 'Safari ' + (m ? m[1].split('.')[0] : '');
      } else if (ua.includes('Edg/')) {
        const m = ua.match(/Edg\/([\d.]+)/);
        browser = 'Edge ' + (m ? m[1].split('.')[0] : '');
      }
      // OS
      if (ua.includes('Windows')) os = 'Windows';
      else if (ua.includes('Mac OS')) os = 'macOS';
      else if (ua.includes('Linux') && !ua.includes('Android')) os = 'Linux';
      else if (ua.includes('Android')) os = 'Android';
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
      return { browser, os, full: ua.substring(0, 120) + (ua.length > 120 ? '…' : '') };
    },

    async cargarDeviceLabels() {
        try {
          const token = localStorage.getItem('token');
          const res = await axiosInstance.post('/api/device_labels_list', { token });
          const data = res.data?.data || res.data;
          if (data?.labels) {
            const map = {};
            for (const lb of data.labels) {
              map[lb.device_id] = { label: lb.label, id: lb.id };
            }
            this.deviceLabels = map;
          }
        } catch (e) {
          // Silently fail — labels are optional
          this.deviceLabels = {};
        }
      },

    getDeviceLabel(deviceId) {
        if (!deviceId) return null;
        const entry = this.deviceLabels[deviceId];
        return entry ? entry.label : null;
    },

    /* ── Label editor ── */
    toggleLabelEditor() {
      this.labelInput = this.getDeviceLabel(this.selectedDevice) || '';
      this.editingLabel = !this.editingLabel;
      this.labelError = '';
      this.$nextTick(() => {
        if (this.editingLabel && this.$refs.labelInputRef) {
          this.$refs.labelInputRef.focus();
          this.$refs.labelInputRef.select();
        }
      });
    },

    cancelLabelEdit() {
      this.editingLabel = false;
      this.labelInput = '';
      this.labelError = '';
    },

    async saveDeviceLabel() {
      if (!this.selectedDevice || !this.labelInput.trim()) return;
      this.savingLabel = true;
      this.labelError = '';
      try {
        const token = localStorage.getItem('token');
        const res = await axiosInstance.post('/api/device_label_save', {
          token,
          device_id: this.selectedDevice,
          label: this.labelInput.trim(),
        });
        const newId = res.data?.data?.id || res.data?.id;
        if (res.data?.success || newId) {
          this.deviceLabels[this.selectedDevice] = { label: this.labelInput.trim(), id: newId };
          this.editingLabel = false;
          this.labelInput = '';
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Error al guardar';
        this.labelError = msg;
      } finally {
        this.savingLabel = false;
      }
    },

    async deleteDeviceLabel() {
      if (!this.selectedDevice || !this.getDeviceLabel(this.selectedDevice)) return;
      if (!confirm(`¿Eliminar etiqueta "${this.getDeviceLabel(this.selectedDevice)}" para este dispositivo?`)) return;
      this.savingLabel = true;
      this.labelError = '';
      try {
        const token = localStorage.getItem('token');
        const labelEntry = this.deviceLabels[this.selectedDevice];
        if (!labelEntry?.id) {
          // Sin ID conocido, solo limpiar local
          delete this.deviceLabels[this.selectedDevice];
          return;
        }
        const res = await axiosInstance.post('/api/device_label_kill', {
          token,
          id: labelEntry.id,
        });
        if (res.data?.success) {
          delete this.deviceLabels[this.selectedDevice];
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Error al eliminar';
        this.labelError = msg;
      } finally {
        this.savingLabel = false;
      }
    },

    
    /** Returns 'critico', 'riesgo', or 'optimo' based on conexion pct */
    canalState(canal) {
      if (!canal || !canal.sucursales || !canal.conectados) return 'critico';
      const pct = Math.round((canal.conectados / canal.sucursales) * 100);
      if (pct >= 90) return 'optimo';
      if (pct >= 50) return 'riesgo';
      return 'critico';
    },

    toggleGroup(key) {
      this.collapsedGroups[key] = !this.collapsedGroups[key];
    },

    /* ── Health helpers for channel cards ── */
    healthLabel(canal) {
      const state = this.canalState(canal);
      if (state === 'optimo') return 'Optimo';
      if (state === 'riesgo') return 'En riesgo';
      return 'Critico';
    },

    healthTextClass(canal) {
      return this.healthIndicatorClass(canal);
    },

    healthBarClass(canal) {
      return this.healthIndicatorClass(canal);
    },

    healthIndicatorClass(canal) {
      const state = this.canalState(canal);
      if (state === 'optimo') return 'is-green';
      if (state === 'riesgo') return 'is-yellow';
      return 'is-red';
    },

    healthCardClass(canal) {
      const state = this.canalState(canal);
      return 'cdm-card--' + state;
    },

    /* ── Health score helpers ── */
    healthScoreClass(score) {
      if (score >= 80) return 'is-green';
      if (score >= 50) return 'is-yellow';
      return 'is-red';
    },

    /* ── Timeline dot helpers ── */
    healthScoreTooltip(ses) {
      if (!ses || ses.health_score === undefined || ses.health_score === null) return '';
      const hb = ses.heartbeats || 0;
      const esp = ses.heartbeats_esperados || 1;
      return `Health ${ses.health_score}%: ${hb} de ${esp} heartbeats esperados`;
    },

    /* ── Auto-refresh ── */
    iniciarAutoRefresh() {
      this.detenerAutoRefresh();
      // Update 'now' cada 1s para timeAgo reactivo suave
      this.timeInterval = setInterval(() => {
        this.now = Date.now();
      }, 1000);
      // Recarga datos cada 30s (silenciosa, sin loading ni flash)
      if (this.autoRefreshEnabled) {
        this.refreshTimer = setInterval(() => {
          this.recargarSilenciosa();
        }, 30000);
      }
    },

    toggleAutoRefresh() {
      this.autoRefreshEnabled = !this.autoRefreshEnabled;
      if (this.autoRefreshEnabled) {
        // Reanudar: crear nuevo timer de recarga
        this.detenerAutoRefresh();
        this.timeInterval = setInterval(() => {
          this.now = Date.now();
        }, 1000);
        this.refreshTimer = setInterval(() => {
          this.recargarSilenciosa();
        }, 30000);
        // Recarga inmediata al reanudar
        this.recargarSilenciosa();
      } else {
        // Pausar: solo detener recarga, mantener timeInterval para timeAgo
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer);
          this.refreshTimer = null;
        }
      }
    },

    detenerAutoRefresh() {
      if (this.timeInterval) {
        clearInterval(this.timeInterval);
        this.timeInterval = null;
      }
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    /* ── Logo helpers (mismo patrón que Canales/Contactos) ── */
    getLogoUrl(canal) {
      if (canal && canal.mobileimage) return 'https://vybroo.net/images' + canal.mobileimage;
      return '';
    },

    logoBackgroundStyle(canal) {
      if (!canal || !canal.color) return { backgroundColor: '#3f3f46' };
      const hex = String(canal.color).replace(/^#/, '').toLowerCase();
      if (/^[0-9a-f]{3}$/.test(hex) || /^[0-9a-f]{6}$/.test(hex)) {
        return { backgroundColor: '#' + hex };
      }
      return { backgroundColor: '#3f3f46' };
    },

    /** Título del dispositivo siguiendo la lógica de conexiones.vue */
    displayDeviceTitle(dev) {
      if (!dev) return 'Sin Identificar';
      const idName = dev.sucursal_nombre || '';
      const varName = dev.variante_nombre || '';
      if (idName || varName) {
        if (idName === varName) return 'ID/VAR: ' + idName;
        if (idName && varName) return 'ID: ' + idName + ' VAR: ' + varName;
        if (idName) return 'ID: ' + idName;
        if (varName) return 'VAR: ' + varName;
      }
      // Fallback: mostrar device_id como identidad única; IP como último recurso
      if (dev.device_id) return 'ID: ' + this.displayDeviceId(dev.device_id);
      if (dev.ip) return 'IP: ' + dev.ip;
      return 'Sin Identificar';
    },

    /** Muestra device_id limpio: quita prefijo y extrae segmento corto */
    displayDeviceId(id) {
      if (!id) return null;
      // Quitar prefijo 'dev_', 'legacy_' o 'mem_'
      let clean = id.replace(/^(dev_|legacy_|mem_)/, '');
      // Si tiene formato UUID (con guiones), tomar solo el primer segmento
      if (clean.includes('-')) {
        clean = clean.split('-')[0];
      } else if (clean.length > 10) {
        // Para hashes largos (legacy_md5), truncar a 8
        clean = clean.substring(0, 8);
      }
      return clean;
    },

    formatDuracion(minutos) {
      if (!minutos || minutos < 1) return '< 1 min';
      if (minutos < 60) return `${minutos} min`;
      const h = Math.floor(minutos / 60);
      const m = minutos % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },

    /** Recarga silenciosa: actualiza datos sin mostrar loading ni limpiar arrays */
    async recargarSilenciosa() {
      if (this.loading || this.isRefreshing) return;
      this.isRefreshing = true;
      const token = localStorage.getItem('token');

      if (this.selectedDevice) {
        try {
          const res = await axiosInstance.get('/api/kpi_ops_sesiones_device', {
            params: { device_id: this.selectedDevice, token },
          });
          const data = res.data?.data || res.data;
          this.selectedDeviceData = data.device || null;
          this.cargarOfflineEvents(this.selectedDevice);
        } catch (e) {
          console.warn('[ConectadosDetalleModal] Silent refresh failed:', e?.message || e);
          if (e?.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        } finally {
          this.isRefreshing = false;
        }
      } else if (this.selectedStream) {
        try {
          const res = await axiosInstance.get('/api/kpi_ops_conectados_detalle', {
            params: { stream: this.selectedStream, token },
          });
          const data = res.data?.data || res.data;
          this.dispositivos = data.dispositivos || [];
        } catch (e) {
          console.warn('[ConectadosDetalleModal] Silent refresh failed:', e?.message || e);
          if (e?.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
        } finally {
          this.isRefreshing = false;
        }
      } else {
        this.isRefreshing = false;
      }
      this.now = Date.now();
    },

    /** Recarga la vista actual (con loading, para acciones del usuario) */
    recargarVistaActual() {
      if (this.loading) return;
      if (this.selectedDevice) {
        this.cargarSesiones(this.selectedDevice);
      } else if (this.selectedStream) {
        this.cargarDetalle(this.selectedStream, this.radioName);
      }
      this.now = Date.now();
    },


    /* ── Tiempo relativo ── */
    timeAgo(iso) {
      if (!iso) return '—';
      const secs = Math.floor((this.now - new Date(iso).getTime()) / 1000);
      if (secs < 5) return 'ahora';
      if (secs < 60) return `${secs}s`;
      const mins = Math.floor(secs / 60);
      if (mins < 60) return `${mins}min`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ${mins % 60}min`;
      return `${Math.floor(hrs / 24)}d`;
    },

    formatTime(dt) {
      if (!dt) return '—';
      const d = new Date(dt);
      return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    },

    formatTimeCompact(dt) {
      if (!dt) return '—';
      const d = new Date(dt);
      return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
    },

    formatDateTime(dt) {
      if (!dt) return '';
      const d = new Date(dt);
      return d.toLocaleString('es-MX', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    },

    /* ── Bars ── */
    canalConexionPct(canal) {
      if (!canal || !canal.sucursales || !canal.conectados) return 0;
      return Math.min(100, Math.round((canal.conectados / canal.sucursales) * 100));
    },

    /* ── Scroll position ── */
    guardarScroll() {
      const el = this.$refs?.cdmBody;
      if (el && el.scrollTop !== undefined) {
        this.scrollPositions[this.viewKey] = el.scrollTop;
      }
    },

    restaurarScroll() {
      this.$nextTick(() => {
        const el = this.$refs?.cdmBody;
        const saved = this.scrollPositions[this.viewKey];
        if (el && saved !== undefined) el.scrollTop = saved;
      });
    },

    /* ── Carga de datos ── */
    async cargarDetalle(stream, name, sucursales) {
      this.guardarScroll();
      this.selectedStream = stream;
      this.radioName = name;
      this.streamSucursales = (sucursales != null && sucursales !== undefined) ? sucursales : null;
      this.selectedDevice = null;
      this.deviceSearch = '';
      this.deviceFilter = 'all';
      this.loading = true;
      this.error = null;
      this.dispositivos = [];
      try {
        const token = localStorage.getItem('token');
        const res = await axiosInstance.get('/api/kpi_ops_conectados_detalle', {
          params: { stream, token },
        });
        const data = res.data?.data || res.data;
        this.dispositivos = data.dispositivos || [];
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        const msg = e?.response?.data?.message || e?.message || '';
        this.error = msg
          ? `Error: ${msg}`
          : 'Error al cargar detalle de dispositivos';
        this.dispositivos = [];
      } finally {
        this.loading = false;
        this.restaurarScroll();
      }
    },

    async cargarSesiones(deviceId) {
      this.guardarScroll();
      this.selectedDevice = deviceId;
      this.loading = true;
      this.error = null;
      this.selectedDeviceData = null;
      try {
        const token = localStorage.getItem('token');
        const res = await axiosInstance.get('/api/kpi_ops_sesiones_device', {
          params: { device_id: deviceId, token },
        });
        const data = res.data?.data || res.data;
      this.selectedDeviceData = data.device || null;
      // Cargar eventos offline en paralelo
      this.cargarOfflineEvents(deviceId);
      this.cargarDeviceFingerprint();
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        const msg = e?.response?.data?.message || e?.message || '';
        this.error = msg
          ? `Error: ${msg}`
          : 'Error al cargar datos del dispositivo';
        this.selectedDeviceData = null;
      } finally {
        this.loading = false;
      }
    },

    async cargarOfflineEvents(deviceId) {
      this.offlineData = null;
      try {
        const token = localStorage.getItem('token');
        const res = await axiosInstance.get('/api/kpi_ops_offline_events', {
          params: { device_id: deviceId, token },
        });
        const data = res.data?.data || res.data;
        this.offlineData = data?.offline_events !== undefined ? data : null;
      } catch (e) {
        // Silently fail — no romper la vista si el endpoint falla
        this.offlineData = null;
      }
    },

    volverADispositivos() {
      this.selectedDevice = null;
      this.selectedDeviceData = null;
      this.offlineData = null;
      this.restaurarScroll();
    },

    irARaices() {
      this.guardarScroll();
      this.selectedStream = null;
      this.radioName = null;
      this.dispositivos = [];
      this.streamSucursales = null;
      this.selectedDevice = null;
      this.selectedDeviceData = null;
      this.offlineData = null;
      this.error = null;
      this.searchQuery = '';
      this.filterState = 'all';
      this.collapsedGroups = { critico: false, riesgo: false, optimo: false };
      this.deviceSearch = '';
      this.deviceFilter = 'all';
      this.$nextTick(() => this.restaurarScroll());
    },

    offlineBarWidth(mins) {
      if (!mins || mins < 1) return '0%';
      // Escala logaritmica suave: 8min = ~60%, 30min = ~85%, 60min+ = 100%
      const pct = Math.min(100, 20 + Math.log2(mins + 1) * 14);
      return Math.round(pct) + '%';
    },

    onDialogVisibleChange(val) {
      // PrimeVue cierra el Dialog internamente si el usuario hace clic
      // fuera (dismissableMask=true) o presiona Escape. Sincronizar con el padre.
      if (!val && this.visible) {
        this.cerrarModal();
      }
    },

    cerrarModal() {
      this.detenerAutoRefresh();
      this.irARaices();
      this.$emit('update:visible', false);
      this.$emit('close');
    },
  },
};
</script>

<style>
/* ═══════════════════════════════════════════
   Conectados Detalle Modal — Estilo profesional v3
   Soporte claro/oscuro vía variables CSS PrimeVue
   ═══════════════════════════════════════════ */

/* ── Dialog overrides ── */
.conectados-detalle-modal :deep(.p-dialog) {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0,0,0,0.18);
}
.dark .conectados-detalle-modal :deep(.p-dialog) {
  box-shadow: 0 25px 80px rgba(0,0,0,0.5);
}
.conectados-detalle-modal :deep(.p-dialog-header) {
  display: none;
}
.conectados-detalle-modal :deep(.p-dialog-content) {
  padding: 0;
  border-radius: 16px;
  background: var(--surface-card, #fff);
  overflow: hidden;
}
.dark .conectados-detalle-modal :deep(.p-dialog-content) {
  background: #18181b;
}


/* ── KPI Hero gradient ── */
.dark .conectados-detalle-modal .kpi-mus-hero {
  background: linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.04) 100%);
  border-color: rgba(16,185,129,0.15);
}

/* ── Header ── */
.cdm-refresh-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-color-secondary, #64748b);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: all 0.15s;
  flex-shrink: 0;
}
.cdm-refresh-btn:hover:not(:disabled) {
  background: rgba(0,0,0,0.06);
  color: var(--primary-color, #3b82f6);
}
.dark .cdm-refresh-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.08);
  color: #60a5fa;
}
.cdm-refresh-btn.is-loading i {
  animation: cdm-spin 0.8s linear infinite;
}
@keyframes cdm-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
/* ── Breadcrumb ── */
.cdm-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1.5rem;
  font-size: 0.8rem;
  border-bottom: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-ground, #f8fafc);
}
.dark .cdm-breadcrumb {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.06);
}
.cdm-breadcrumb-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--text-color-secondary, #64748b);
  cursor: pointer;
  transition: color 0.15s;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
  white-space: nowrap;
}
.cdm-breadcrumb-item:hover:not(:disabled) {
  color: var(--primary-color, #3b82f6);
}
.cdm-breadcrumb-item.is-active {
  color: var(--text-color, #1e293b);
  font-weight: 600;
  cursor: default;
}
.dark .cdm-breadcrumb-item.is-active {
  color: #e4e4e7;
}
.cdm-breadcrumb-item:disabled {
  cursor: default;
  opacity: 0.8;
}
.cdm-breadcrumb-device-id {
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.78rem;
}
.cdm-breadcrumb-sep {
  font-size: 0.6rem;
  color: var(--text-color-secondary, #94a3b8);
}



/* ── Body ── */
.cdm-body {
  padding: 1.25rem 1.5rem 1.5rem;
  max-height: 72vh;
  overflow-y: auto;
  scrollbar-width: thin;
}
.cdm-body::-webkit-scrollbar {
  width: 5px;
}
.cdm-body::-webkit-scrollbar-track {
  background: transparent;
}
.cdm-body::-webkit-scrollbar-thumb {
  background: var(--surface-border, #d1d5db);
  border-radius: 3px;
}
.dark .cdm-body::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.12);
}

/* ── Animations ── */
.cdm-fade-slide-enter-active,
.cdm-fade-slide-leave-active {
  transition: all 0.2s ease;
}
.cdm-fade-slide-enter-from,
.cdm-fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── View transitions ── */
.cdm-view-enter-active,
.cdm-view-leave-active {
  transition: all 0.2s ease;
}
.cdm-view-enter-from {
  opacity: 0;
  transform: translateX(12px);
}
.cdm-view-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

/* ── Group collapse/expand slide-down ── */
.cdm-group-collapse-enter-active,
.cdm-group-collapse-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.cdm-group-collapse-enter-from,
.cdm-group-collapse-leave-to {
  opacity: 0;
  transform: translateY(-8px) scaleY(0.95);
  transform-origin: top center;
}

/* ── Toolbar ── */
.cdm-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.cdm-toolbar-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cdm-toolbar-row .cdm-search {
  flex: 1;
}
.cdm-search {
  position: relative;
  display: flex;
  align-items: center;
}
.cdm-search-icon {
  position: absolute;
  left: 0.65rem;
  font-size: 0.78rem;
  color: var(--text-color-secondary, #94a3b8);
  pointer-events: none;
}
.cdm-search-input {
  width: 100%;
  padding: 0.45rem 0.75rem 0.45rem 2rem;
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-card, #fff);
  color: var(--text-color, #1e293b);
  font-size: 0.82rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
}
.dark .cdm-search-input {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: #e4e4e7;
}
.cdm-search-input:focus {
  border-color: var(--primary-color, #3b82f6);
}
.cdm-search-input::placeholder {
  color: var(--text-color-secondary, #94a3b8);
}

/* ── View toggle (lista/grid) ── */
.cdm-view-toggle {
  display: flex;
  gap: 2px;
  background: var(--surface-ground, #f1f5f9);
  padding: 2px;
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e2e8f0);
  flex-shrink: 0;
}
.dark .cdm-view-toggle {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.08);
}
.cdm-view-btn {
  width: 30px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-color-secondary, #94a3b8);
  cursor: pointer;
  font-size: 0.78rem;
  transition: all 0.15s;
}
.cdm-view-btn:hover {
  color: var(--primary-color, #3b82f6);
}
.cdm-view-btn.is-active {
  background: var(--surface-card, #fff);
  color: var(--primary-color, #3b82f6);
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.dark .cdm-view-btn.is-active {
  background: rgba(255,255,255,0.1);
  color: #60a5fa;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

/* ── Chip filters (pill-shaped, estilo KPI) ── */
.cdm-chip-group {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.cdm-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #475569;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  user-select: none;
  transition: border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
}
.cdm-chip input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.cdm-chip:hover {
  transform: translateY(-1px);
}
.cdm-chip.is-active {
  box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.4);
}
.cdm-chip:nth-of-type(1).is-active {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(219, 234, 254, 0.9);
  color: #1d4ed8;
}
.cdm-chip:nth-of-type(2).is-active {
  border-color: rgba(34, 197, 94, 0.6);
  background: rgba(220, 252, 231, 0.95);
  color: #15803d;
}
.cdm-chip:nth-of-type(3).is-active {
  border-color: rgba(217, 119, 6, 0.55);
  background: rgba(254, 243, 199, 0.95);
  color: #92400e;
}
.cdm-chip:nth-of-type(4).is-active {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(254, 226, 226, 0.95);
  color: #b91c1c;
}
.dark .cdm-chip {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #d4d4d8;
}
.dark .cdm-chip:hover {
  border-color: rgba(96, 165, 250, 0.55);
  background: rgba(30, 41, 59, 0.85);
  color: #bfdbfe;
}
.dark .cdm-chip:nth-of-type(1).is-active {
  border-color: rgba(96, 165, 250, 0.75);
  background: rgba(30, 58, 138, 0.3);
  color: #dbeafe;
}
.dark .cdm-chip:nth-of-type(2).is-active {
  border-color: rgba(74, 222, 128, 0.75);
  background: rgba(20, 83, 45, 0.28);
  color: #dcfce7;
}
.dark .cdm-chip:nth-of-type(3).is-active {
  border-color: rgba(251, 191, 36, 0.7);
  background: rgba(113, 63, 18, 0.28);
  color: #fde68a;
}
.dark .cdm-chip:nth-of-type(4).is-active {
  border-color: rgba(248, 113, 113, 0.7);
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
}

/* Explicit chip color classes (overrides nth-of-type) */
.cdm-chip.is-optimo.is-active {
  border-color: rgba(34, 197, 94, 0.6);
  background: rgba(220, 252, 231, 0.95);
  color: #15803d;
}
.dark .cdm-chip.is-optimo.is-active {
  border-color: rgba(74, 222, 128, 0.75);
  background: rgba(20, 83, 45, 0.28);
  color: #dcfce7;
}
.cdm-chip.is-riesgo.is-active,
.cdm-chip.is-paused.is-active {
  border-color: rgba(217, 119, 6, 0.55);
  background: rgba(254, 243, 199, 0.95);
  color: #92400e;
}
.dark .cdm-chip.is-riesgo.is-active,
.dark .cdm-chip.is-paused.is-active {
  border-color: rgba(251, 191, 36, 0.7);
  background: rgba(113, 63, 18, 0.28);
  color: #fde68a;
}
.cdm-chip.is-critico.is-active,
.cdm-chip.is-unidentified.is-active {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(254, 226, 226, 0.95);
  color: #b91c1c;
}
.dark .cdm-chip.is-critico.is-active,
.dark .cdm-chip.is-unidentified.is-active {
  border-color: rgba(248, 113, 113, 0.7);
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
}

/* ── Demo toggle checkbox ── */
.cdm-demo-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  margin-left: auto;
}
.cdm-demo-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.cdm-demo-toggle-check {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(148, 163, 184, 0.5);
  border-radius: 4px;
  background: rgba(255,255,255,0.94);
  transition: all 0.18s ease;
  flex-shrink: 0;
  position: relative;
}
.dark .cdm-demo-toggle-check {
  background: rgba(24,24,27,0.75);
  border-color: rgba(113,113,122,0.5);
}
.cdm-demo-toggle input:checked + .cdm-demo-toggle-check {
  border-color: rgba(239,68,68,0.55);
  background: rgba(254,226,226,0.95);
}
.dark .cdm-demo-toggle input:checked + .cdm-demo-toggle-check {
  border-color: rgba(248,113,113,0.7);
  background: rgba(127,29,29,0.28);
}
.cdm-demo-toggle input:checked + .cdm-demo-toggle-check::after {
  content: '✓';
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #b91c1c;
  line-height: 1;
}
.dark .cdm-demo-toggle input:checked + .cdm-demo-toggle-check::after {
  color: #fecaca;
}
.cdm-demo-toggle-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
  white-space: nowrap;
}
.dark .cdm-demo-toggle-label {
  color: #a1a1aa;
}

/* ── State (empty/error) ── */
.cdm-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
  gap: 0.5rem;
}
.cdm-state-icon {
  font-size: 1.5rem;
  color: var(--text-color-secondary, #94a3b8);
  opacity: 0.5;
}
.dark .cdm-state-icon {
  color: #52525b;
}
.cdm-state-text {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-color-secondary, #64748b);
}
.dark .cdm-state-text {
  color: #a1a1aa;
}
.cdm-state--error .cdm-state-icon {
  color: #ef4444;
  opacity: 0.7;
}
.cdm-state-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}
.cdm-state-btn {
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-card, #fff);
  color: var(--text-color, #1e293b);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
}
.cdm-state-btn:hover {
  border-color: var(--primary-color, #3b82f6);
  color: var(--primary-color, #3b82f6);
}
.dark .cdm-state-btn {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: #e4e4e7;
}
.dark .cdm-state-btn:hover {
  border-color: var(--primary-color, #3b82f6);
  color: var(--primary-color, #60a5fa);
}

/* ── Skeleton (card-shaped) ── */
.cdm-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
}
.cdm-skel-card {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-card, #fff);
  overflow: hidden;
}
.dark .cdm-skel-card {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.07);
}
.cdm-skel-card-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}
.cdm-skel-circle {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--surface-border, #e2e8f0);
  animation: cdm-shimmer 1.5s ease-in-out infinite;
}
.dark .cdm-skel-circle {
  background: rgba(255,255,255,0.08);
}
.cdm-skel-bar-v {
  width: 6px;
  height: 20px;
  border-radius: 3px;
  background: var(--surface-border, #e2e8f0);
  animation: cdm-shimmer 1.5s ease-in-out infinite;
}
.dark .cdm-skel-bar-v {
  background: rgba(255,255,255,0.06);
}
.cdm-skel-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.cdm-skel-card-right {
  text-align: right;
  min-width: 90px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
}
.cdm-skel-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--surface-border, #e2e8f0) 25%, rgba(255,255,255,0.4) 50%, var(--surface-border, #e2e8f0) 75%);
  background-size: 200% 100%;
  animation: cdm-shimmer 1.5s ease-in-out infinite;
}
.dark .cdm-skel-line {
  background: linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%);
  background-size: 200% 100%;
}
.cdm-skel-line--lg { width: 70%; }
.cdm-skel-line--md { width: 50%; }
.cdm-skel-line--sm { width: 35%; }
.cdm-skel-line--num { width: 40px; height: 18px; }
.cdm-skel-line--pct { width: 30px; height: 8px; }
.cdm-skel-bar-track {
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: var(--surface-border, #e2e8f0);
  overflow: hidden;
}
.dark .cdm-skel-bar-track {
  background: rgba(255,255,255,0.08);
}
.cdm-skel-bar-fill {
  width: 60%;
  height: 100%;
  border-radius: 2px;
  background: var(--primary-color, #3b82f6);
}
.dark .cdm-skel-bar-fill {
  background: #60a5fa;
}
@keyframes cdm-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Card list ── */
.cdm-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.cdm-card {
  border-radius: 10px;
  border: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-card, #fff);
  cursor: pointer;
  transition: all 0.15s;
  overflow: hidden;
}
.dark .cdm-card {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.07);
}
.cdm-card:hover {
  border-color: var(--primary-color, #3b82f6);
  background: rgba(59,130,246,0.03);
}
.dark .cdm-card:hover {
  border-color: var(--primary-color, #60a5fa);
  background: rgba(59,130,246,0.06);
}
.cdm-card-body {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 0.85rem;
}
.cdm-card--device .cdm-card-body {
  padding: 0.55rem 0.75rem;
}
.cdm-card-info {
  flex: 1;
  min-width: 0;
}
.cdm-card-title {
  display: block;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
}
.dark .cdm-card-title {
  color: #e4e4e7;
}
.cdm-card-sub {
  display: block;
  font-size: 0.7rem;
  color: var(--text-color-secondary, #94a3b8);
  margin-top: 1px;
}
.dark .cdm-card-sub {
  color: #71717a;
}

.cdm-card-arrow {
  color: var(--text-color-secondary, #94a3b8);
  font-size: 0.7rem;
  flex-shrink: 0;
  opacity: 0.5;
}



/* ── Logo circular para canales (list view) ── */
.cdm-channel-logo {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s;
}
.cdm-card-channel:hover .cdm-channel-logo {
  transform: scale(1.08);
}
.cdm-channel-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.cdm-channel-logo-fallback {
  color: rgba(255,255,255,0.5);
  font-size: 0.85rem;
}

/* ── Stats compactos para canales ── */
.cdm-channel-stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  flex-shrink: 0;
  min-width: 84px;
}

.cdm-channel-stat-row {
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
}

.cdm-channel-sucursales {
  font-size: 0.6rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  white-space: nowrap;
}
.dark .cdm-channel-sucursales {
  color: #71717a;
}

.cdm-channel-pct {
  font-size: 0.6rem;
  font-weight: 600;
  color: #10b981;
  line-height: 1;
}
.dark .cdm-channel-pct {
  color: #6ee7b7;
}

.cdm-grid-card-pct {
  font-size: 0.6rem;
  font-weight: 600;
  color: #10b981;
  margin-top: 0.2rem;
  text-align: center;
}
.dark .cdm-grid-card-pct {
  color: #6ee7b7;
}
.cdm-channel-count {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--primary-color, #3b82f6);
  line-height: 1.2;
}
.dark .cdm-channel-count {
  color: #60a5fa;
}
.cdm-channel-bar-track {
  width: 100%;
  height: 3px;
  background: var(--surface-border, #e2e8f0);
  border-radius: 999px;
  overflow: hidden;
}
.dark .cdm-channel-bar-track {
  background: rgba(255,255,255,0.08);
}
.cdm-channel-bar-fill {
  height: 100%;
  background: var(--primary-color, #3b82f6);
  border-radius: 999px;
  transition: width 0.3s ease;
  min-width: 2px;
}
.dark .cdm-channel-bar-fill {
  background: #60a5fa;
}

/* ── Grid view ── */
.cdm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 0.5rem;
}
.cdm-grid-card {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-card, #fff);
  cursor: pointer;
  transition: all 0.15s;
  overflow: hidden;
}
.dark .cdm-grid-card {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.07);
}
.cdm-grid-card:hover {
  border-color: var(--primary-color, #3b82f6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.dark .cdm-grid-card:hover {
  border-color: var(--primary-color, #60a5fa);
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}
.cdm-grid-card-top {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1rem 0.5rem;
}
.cdm-grid-card-logo {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s;
}
.cdm-grid-card:hover .cdm-grid-card-logo {
  transform: scale(1.08);
}
.cdm-grid-card-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.cdm-grid-card-logo-fallback {
  color: rgba(255,255,255,0.5);
  font-size: 1.1rem;
}
.cdm-grid-card-body {
  padding: 0.25rem 0.85rem 0.85rem;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.cdm-grid-card-name {
  font-weight: 600;
  font-size: 0.82rem;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dark .cdm-grid-card-name {
  color: #e4e4e7;
}
.cdm-grid-card-sub {
  font-size: 0.65rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  margin-bottom: 0.35rem;
}
.dark .cdm-grid-card-sub {
  color: #71717a;
}
.cdm-grid-card-bar-track {
  height: 4px;
  background: var(--surface-border, #e2e8f0);
  border-radius: 2px;
  overflow: hidden;
  margin-top: auto;
}
.dark .cdm-grid-card-bar-track {
  background: rgba(255,255,255,0.08);
}
.cdm-grid-card-bar-fill {
  height: 100%;
  background: var(--primary-color, #3b82f6);
  border-radius: 2px;
  transition: width 0.3s ease;
}
.dark .cdm-grid-card-bar-fill {
  background: #60a5fa;
}

/* ── Device-specific (list view) ── */

/* Status column: online dot + health mini-bar */
.cdm-device-status-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  padding-top: 2px;
}

/* Online dot */
.cdm-online-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: all 0.3s;
}
.cdm-online-dot.is-online {
  background: #22c55e;
  box-shadow: 0 0 0 2px rgba(34,197,94,0.2);
}
.cdm-online-dot.is-offline {
  background: var(--text-color-secondary, #a1a1aa);
  opacity: 0.4;
}

/* Health mini-bar (vertical) */
.cdm-health-indicator {
  width: 6px;
  height: 20px;
  border-radius: 3px;
  background: var(--surface-border, #e2e8f0);
  overflow: hidden;
  flex-shrink: 0;
}
.dark .cdm-health-indicator {
  background: rgba(255,255,255,0.06);
}
.cdm-health-fill {
  width: 100%;
  border-radius: 3px;
  transition: height 0.3s;
  position: relative;
  bottom: 0;
}
.cdm-health-indicator.is-green .cdm-health-fill {
  background: #22c55e;
}
.cdm-health-indicator.is-yellow .cdm-health-fill {
  background: #d97706;
}
.cdm-health-indicator.is-red .cdm-health-fill {
  background: #ef4444;
}

/* Primary row: name + badges */
.cdm-device-primary-row {
  display: flex;
  align-items: center;
  gap: 0.3rem;
}
.cdm-device-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
}
.dark .cdm-device-name {
  color: #e4e4e7;
}

/* Status badge (▶ Reproduciendo / ⏸ En pausa) */
.cdm-device-status-badge {
  font-size: 0.68rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}
.cdm-device-status-badge.is-playing {
  color: #22c55e;
}
.cdm-device-status-badge.is-paused {
  color: #d97706;
  font-weight: 600;
}
.dark .cdm-device-status-badge.is-paused {
  color: #fbbf24;
}

/* Tiny device ID in meta row */
.cdm-device-id-tiny {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.6rem;
  color: var(--text-color-secondary, #94a3b8);
  opacity: 0.4;
  cursor: help;
  margin-left: auto;
}

/* Secondary row: categoria · audio */
.cdm-device-secondary-row {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.72rem;
  color: var(--text-color-secondary, #64748b);
  margin-top: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.dark .cdm-device-secondary-row {
  color: #a1a1aa;
}
.cdm-device-cat {
  flex-shrink: 0;
}
.cdm-device-audio {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-style: italic;
}
.cdm-device-audio--none {
  opacity: 0.5;
  font-style: normal;
}

/* Version chip */
.cdm-device-version {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.65rem;
  color: var(--text-color-secondary, #94a3b8);
  background: var(--surface-ground, #f1f5f9);
  padding: 0 4px;
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
}
.dark .cdm-device-version {
  background: rgba(255,255,255,0.06);
  color: #71717a;
}

/* Compact IP (fallback when no sucursal) */
.cdm-device-ip-compact {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.65rem;
  color: var(--text-color-secondary, #94a3b8);
  opacity: 0.5;
}

/* Meta row: time ago */
.cdm-device-meta-row {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.7rem;
  color: var(--text-color-secondary, #94a3b8);
  margin-top: 1px;
}
.dark .cdm-device-meta-row {
  color: #71717a;
}
.cdm-meta-icon {
  font-size: 0.6rem;
  margin-right: 1px;
}
.cdm-time-ago {
  font-weight: 500;
}

/* Right column: health % + arrow */
.cdm-device-right-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.15rem;
  flex-shrink: 0;
  min-width: 40px;
}
.cdm-device-health-num {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
}
.cdm-device-health-num.is-green { color: #22c55e; }
.cdm-device-health-num.is-yellow { color: #d97706; }
.cdm-device-health-num.is-red { color: #ef4444; }

/* Detail sep */
.cdm-detail-sep {
  opacity: 0.3;
  margin: 0 1px;
}

/* ── Badge (legacy only) ── */
.cdm-badge {
  font-size: 0.6rem;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  flex-shrink: 0;
}
.cdm-badge--amber {
  background: rgba(251,191,36,0.12);
  color: #d97706;
}
.dark .cdm-badge--amber {
  background: rgba(251,191,36,0.15);
  color: #fbbf24;
}

/* ── Group header ── */
.cdm-group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.5rem 0.15rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.dark .cdm-group-header {
  color: #a1a1aa;
}
.cdm-group-count {
  font-weight: 400;
  font-size: 0.65rem;
  opacity: 0.6;
  margin-left: auto;
}

/* ── Device Dashboard ── */
.cdm-device-hero {
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e2e8f0);
  border-radius: 12px;
  padding: 1rem 1.15rem;
  margin-bottom: 0.65rem;
  transition: border-color 0.3s;
}
.dark .cdm-device-hero {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.07);
}
.cdm-device-hero.is-offline {
  border-color: rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.03);
}
.dark .cdm-device-hero.is-offline {
  border-color: rgba(239, 68, 68, 0.25);
  background: rgba(239, 68, 68, 0.05);
}

.cdm-device-hero-status-row {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-bottom: 0.15rem;
}
.cdm-device-hero-status-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
}
.dark .cdm-device-hero-status-label {
  color: #a1a1aa;
}
.cdm-device-hero-status-label.is-offline {
  color: #ef4444;
}
.dark .cdm-device-hero-status-label.is-offline {
  color: #f87171;
}
.cdm-hero-offline-time {
  font-size: 0.68rem;
  font-weight: 500;
  color: #ef4444;
}
.dark .cdm-hero-offline-time {
  color: #f87171;
}
.cdm-device-hero-playing {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-color-secondary, #71717a);
}
.cdm-device-hero-playing.is-active {
  color: #22c55e;
}

.cdm-device-hero-title {
  margin: 0 0 0.1rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.cdm-device-label-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.62rem;
  font-weight: 600;
  color: #92400e;
  background: #fef3c7;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.dark .cdm-device-label-badge {
  color: #fbbf24;
  background: rgba(251,191,36,0.12);
}
.cdm-device-label-badge i {
  font-size: 0.55rem;
}

/* ── Label edit buttons ── */
.cdm-label-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.62rem;
  font-weight: 600;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 1px 5px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.4;
  font-family: inherit;
}
.cdm-label-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cdm-label-btn--add {
  color: #10b981;
  border-color: rgba(16,185,129,0.25);
  background: rgba(16,185,129,0.06);
}
.cdm-label-btn--add:hover:not(:disabled) {
  background: rgba(16,185,129,0.12);
  border-color: #10b981;
}
.cdm-label-btn--edit {
  color: var(--primary-color, #3b82f6);
  border-color: transparent;
}
.cdm-label-btn--edit:hover:not(:disabled) {
  background: rgba(59,130,246,0.1);
  border-color: rgba(59,130,246,0.3);
}
.cdm-label-btn--delete {
  color: #ef4444;
  border-color: transparent;
}
.cdm-label-btn--delete:hover:not(:disabled) {
  background: rgba(239,68,68,0.1);
  border-color: rgba(239,68,68,0.3);
}
.cdm-label-btn--save {
  color: #10b981;
  border-color: transparent;
}
.cdm-label-btn--save:hover:not(:disabled) {
  background: rgba(16,185,129,0.1);
  border-color: rgba(16,185,129,0.3);
}
.cdm-label-btn--cancel {
  color: var(--text-color-secondary, #94a3b8);
  border-color: transparent;
}
.cdm-label-btn--cancel:hover:not(:disabled) {
  background: rgba(0,0,0,0.05);
  border-color: rgba(0,0,0,0.1);
}
.dark .cdm-label-btn--cancel:hover:not(:disabled) {
  background: rgba(255,255,255,0.08);
  border-color: rgba(255,255,255,0.15);
}

/* ── Label inline editor ── */
.cdm-label-editor {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  flex-shrink: 0;
  max-width: 100%;
}
.cdm-label-input {
  font-size: 0.72rem;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid var(--surface-border, #d1d5db);
  background: var(--surface-card, #fff);
  color: var(--text-color, #1e293b);
  font-family: inherit;
  outline: none;
  width: 200px;
  transition: border-color 0.15s;
}
.dark .cdm-label-input {
  background: rgba(255,255,255,0.06);
  border-color: rgba(255,255,255,0.15);
  color: #e4e4e7;
}
.cdm-label-input:focus {
  border-color: var(--primary-color, #3b82f6);
}
.cdm-label-input.has-error {
  border-color: #ef4444;
}
.dark .cdm-label-input.has-error {
  border-color: #f87171;
}
.cdm-label-error {
  display: block;
  font-size: 0.65rem;
  color: #ef4444;
  margin-top: 2px;
  line-height: 1.3;
}
.dark .cdm-label-error {
  color: #f87171;
}
.dark .cdm-device-hero-title {
  color: #e4e4e7;
}
.cdm-device-hero-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.2rem;
  font-size: 0.72rem;
  color: var(--text-color-secondary, #94a3b8);
}
.dark .cdm-device-hero-meta {
  color: #71717a;
}

/* Metrics row */
.cdm-device-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin: 0.85rem 0 0;
}
.cdm-device-metrics--3col {
  grid-template-columns: repeat(3, 1fr);
}
.cdm-device-metric {
  text-align: center;
  padding: 0.5rem 0.3rem;
  background: var(--surface-ground, #f8fafc);
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-device-metric {
  background: rgba(255,255,255,0.03);
  border-color: rgba(255,255,255,0.06);
}
.cdm-device-metric-val {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  color: #10b981;
  line-height: 1.2;
}
.cdm-device-metric-val--muted {
  color: var(--primary-color, #3b82f6);
  font-size: 1rem;
}
.dark .cdm-device-metric-val--muted {
  color: #60a5fa;
}
.cdm-device-metric-val.is-green { color: #22c55e; }
.cdm-device-metric-val.is-yellow { color: #d97706; }
.cdm-device-metric-val.is-red { color: #ef4444; }
.cdm-device-metric-lbl {
  display: block;
  font-size: 0.62rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 2px;
}
.dark .cdm-device-metric-lbl {
  color: #71717a;
}
/* Hero meta items */
.cdm-hero-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  white-space: nowrap;
}
.cdm-meta-icon-sm {
  font-size: 0.6rem;
  opacity: 0.6;
}
.cdm-hero-ip {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.68rem;
  opacity: 0.7;
}
.cdm-hero-device-id {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.65rem;
  opacity: 0.4;
  cursor: help;
}

/* Health bar */
.cdm-device-health-bar-wrapper {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-device-health-bar-wrapper {
  border-color: rgba(255,255,255,0.06);
}
.cdm-device-health-bar-track {
  flex: 1;
  height: 6px;
  background: var(--surface-border, #e2e8f0);
  border-radius: 3px;
  overflow: hidden;
  cursor: help;
}
.dark .cdm-device-health-bar-track {
  background: rgba(255,255,255,0.08);
}
.cdm-device-health-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}
.cdm-device-health-bar-fill.is-green { background: #22c55e; }
.cdm-device-health-bar-fill.is-yellow { background: #d97706; }
.cdm-device-health-bar-fill.is-red { background: #ef4444; }
.cdm-device-health-bar-label {
  font-size: 0.68rem;
  color: var(--text-color-secondary, #64748b);
  white-space: nowrap;
  flex-shrink: 0;
}
.dark .cdm-device-health-bar-label {
  color: #a1a1aa;
}
.cdm-device-health-bar-label strong {
  color: var(--text-color, #1e293b);
  font-weight: 700;
}
.dark .cdm-device-health-bar-label strong {
  color: #e4e4e7;
}

/* Audio card */
.cdm-device-audio-card {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.85rem;
  background: linear-gradient(135deg, rgba(16,185,129,0.06), rgba(16,185,129,0.02));
  border: 1px solid rgba(16,185,129,0.15);
  border-radius: 10px;
  margin-bottom: 0.65rem;
}
.dark .cdm-device-audio-card {
  background: linear-gradient(135deg, rgba(16,185,129,0.08), transparent);
  border-color: rgba(16,185,129,0.12);
}
.cdm-device-audio-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16,185,129,0.12);
  color: #10b981;
  font-size: 1rem;
  flex-shrink: 0;
}
.cdm-device-audio-icon .pi-volume-up.is-playing {
  animation: cdm-pulse-audio 2s ease-in-out infinite;
}
@keyframes cdm-pulse-audio {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.cdm-device-audio-info {
  flex: 1;
  min-width: 0;
}
.cdm-device-audio-title {
  display: block;
  font-weight: 600;
  font-size: 0.88rem;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dark .cdm-device-audio-title {
  color: #e4e4e7;
}
.cdm-device-audio-meta {
  font-size: 0.68rem;
  color: var(--text-color-secondary, #64748b);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.15rem;
}
.dark .cdm-device-audio-meta {
  color: #a1a1aa;
}

/* Details grid */
.cdm-device-details {
  border: 1px solid var(--surface-border, #e2e8f0);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.65rem;
}
.dark .cdm-device-details {
  border-color: rgba(255,255,255,0.07);
}
.cdm-device-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.cdm-device-detail-item {
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--surface-border, #e2e8f0);
  border-right: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-device-detail-item {
  border-color: rgba(255,255,255,0.05);
}
.cdm-device-detail-item:nth-child(even) {
  border-right: none;
}
.cdm-device-detail-item:nth-last-child(-n+2) {
  border-bottom: none;
}
.cdm-device-detail-label {
  display: block;
  font-size: 0.6rem;
  font-weight: 600;
  color: var(--text-color-secondary, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 1px;
}
.dark .cdm-device-detail-label {
  color: #71717a;
}
.cdm-device-detail-value {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-color, #1e293b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dark .cdm-device-detail-value {
  color: #e4e4e7;
}
.cdm-device-detail-value--mono {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.72rem;
}
.cdm-device-detail-value--break {
  overflow-wrap: anywhere;
  word-break: normal;
  line-height: 1.4;
  white-space: normal;
}

/* Events */
.cdm-device-events {
  margin-top: 0.25rem;
}
.cdm-device-events-title {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.dark .cdm-device-events-title {
  color: #a1a1aa;
}
.cdm-device-event {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  font-size: 0.72rem;
  background: var(--surface-ground, #f8fafc);
  border-radius: 6px;
  border: 1px solid var(--surface-border, #e2e8f0);
  margin-bottom: 2px;
}
.dark .cdm-device-event {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.05);
}
.cdm-device-event-time {
  font-weight: 600;
  color: var(--text-color, #1e293b);
  white-space: nowrap;
  min-width: 36px;
}
.dark .cdm-device-event-time {
  color: #e4e4e7;
}
.cdm-device-event-song {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-color-secondary, #64748b);
  font-style: italic;
}
.dark .cdm-device-event-song {
  color: #a1a1aa;
}
.cdm-device-event-ip {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.65rem;
  color: var(--text-color-secondary, #94a3b8);
  background: rgba(0,0,0,0.04);
  padding: 0 4px;
  border-radius: 3px;
  white-space: nowrap;
}
.dark .cdm-device-event-ip {
  background: rgba(255,255,255,0.06);
}
.cdm-device-event-status {
  font-size: 0.65rem;
  width: 16px;
  text-align: center;
}
.cdm-device-event-status.is-playing { color: #22c55e; }
.cdm-device-event-status.is-paused { color: var(--text-color-secondary, #94a3b8); }

.cdm-device-note {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.65rem;
  padding: 0.45rem 0.65rem;
  font-size: 0.72rem;
  color: var(--text-color-secondary, #64748b);
  background: var(--surface-ground, #f8fafc);
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-device-note {
  background: rgba(255,255,255,0.03);
  border-color: rgba(255,255,255,0.06);
}

/* ── Offline card state (View 2) ── */
.cdm-card--device.is-offline {
  opacity: 0.7;
  border-color: rgba(239, 68, 68, 0.2);
}
.dark .cdm-card--device.is-offline {
  border-color: rgba(239, 68, 68, 0.15);
}
.cdm-card--device.is-offline .cdm-device-name {
  opacity: 0.6;
}
.cdm-card--device.is-offline .cdm-device-secondary-row {
  opacity: 0.5;
}
.cdm-offline-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: #ef4444;
  flex-shrink: 0;
}
.dark .cdm-offline-label {
  color: #f87171;
}
.cdm-time-ago.is-offline {
  color: #ef4444;
}
.dark .cdm-time-ago.is-offline {
  color: #f87171;
}

/* Online dot shared */
.cdm-timeline-dot.is-alert {
  background: #ef4444;
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239,68,68,0.2);
}

/* ── Confiabilidad de conexión ── */
.cdm-reliability {
  border: 1px solid var(--surface-border, #e2e8f0);
  border-radius: 10px;
  overflow: hidden;
  margin-top: 0.65rem;
}
.dark .cdm-reliability {
  border-color: rgba(255,255,255,0.07);
}
.cdm-reliability-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: var(--surface-ground, #f8fafc);
  border-bottom: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-reliability-header {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.05);
  color: #a1a1aa;
}
.cdm-reliability-icon {
  font-size: 0.75rem;
  color: #f59e0b;
}
.dark .cdm-reliability-icon {
  color: #fbbf24;
}

.cdm-reliability-summary {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem;
  background: var(--surface-card, #fff);
}
.dark .cdm-reliability-summary {
  background: rgba(255,255,255,0.02);
}
.cdm-reliability-stat {
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.cdm-reliability-stat-val {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
}
.dark .cdm-reliability-stat-val {
  color: #e4e4e7;
}
.cdm-reliability-stat-val.is-good { color: #22c55e; }
.cdm-reliability-stat-val.is-mid { color: #d97706; }
.cdm-reliability-stat-val.is-warn { color: #ef4444; }
.cdm-reliability-stat-lbl {
  font-size: 0.62rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.dark .cdm-reliability-stat-lbl {
  color: #71717a;
}
.cdm-reliability-stat-divider {
  width: 1px;
  height: 28px;
  background: var(--surface-border, #e2e8f0);
  flex-shrink: 0;
}
.dark .cdm-reliability-stat-divider {
  background: rgba(255,255,255,0.08);
}

.cdm-reliability-history {
  padding: 0.35rem 0.75rem 0.5rem;
  border-top: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-reliability-history {
  border-color: rgba(255,255,255,0.05);
}
.cdm-reliability-history-title {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-color-secondary, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.3rem;
}
.dark .cdm-reliability-history-title {
  color: #71717a;
}
.cdm-reliability-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem 0;
  font-size: 0.72rem;
}
.cdm-reliability-row-time {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  width: 88px;
  flex-shrink: 0;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.68rem;
  color: var(--text-color, #1e293b);
  font-weight: 600;
}
.dark .cdm-reliability-row-time {
  color: #e4e4e7;
}
.cdm-reliability-row-time-arrow {
  font-size: 0.6rem;
  color: var(--text-color-secondary, #94a3b8);
  opacity: 0.5;
}
.cdm-reliability-row-bar-wrapper {
  flex: 1;
  height: 5px;
  background: var(--surface-border, #e2e8f0);
  border-radius: 3px;
  overflow: hidden;
  min-width: 40px;
}
.dark .cdm-reliability-row-bar-wrapper {
  background: rgba(255,255,255,0.06);
}
.cdm-reliability-row-bar {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, #f59e0b, #ef4444);
  transition: width 0.5s ease;
  min-width: 2px;
}
.cdm-reliability-row-dur {
  min-width: 40px;
  text-align: right;
  font-size: 0.68rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
  flex-shrink: 0;
}
.dark .cdm-reliability-row-dur {
  color: #a1a1aa;
}

.cdm-reliability-row.is-ongoing {
  background: rgba(239, 68, 68, 0.06);
  border-left: 2px solid #ef4444;
  padding-left: 0.5rem;
  margin-left: -0.5rem;
  border-radius: 4px;
}

.cdm-reliability-row.is-ongoing .cdm-reliability-row-bar {
  background: linear-gradient(90deg, #ef4444 0%, #f59e0b 100%);
  animation: cdm-ongoing-pulse 2s ease-in-out infinite;
}

.cdm-reliability-row.is-ongoing .cdm-reliability-row-dur {
  color: #ef4444;
}

.cdm-reliability-row-time-val.is-ongoing {
  color: #ef4444;
  font-weight: 700;
  font-style: italic;
}

@keyframes cdm-ongoing-pulse {
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
}

.cdm-reliability-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  font-size: 0.72rem;
  color: var(--text-color-secondary, #94a3b8);
  border-top: 1px solid var(--surface-border, #e2e8f0);
}
.dark .cdm-reliability-note {
  border-color: rgba(255,255,255,0.05);
  color: #71717a;
}
.cdm-reliability-note-icon {
  color: #22c55e;
  font-size: 0.75rem;
}


.cdm-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #10b981;
  letter-spacing: 0.01em;
}
.dark .cdm-hero-badge {
  color: #6ee7b7;
}
.cdm-hero-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  animation: cdm-pulse 2s ease-in-out infinite;
}
.cdm-hero-updated {
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  padding: 1px 7px;
  border-radius: 4px;
  background: var(--surface-ground, #f1f5f9);
  font-variant-numeric: tabular-nums;
}
.dark .cdm-hero-updated {
  background: rgba(255,255,255,0.05);
  color: #71717a;
}

/* ── Mini hero (View 2: Dispositivos) ── */
.cdm-hero.cdm-hero--sm {
  padding: 0.85rem 0.65rem;
  margin-bottom: 0.65rem;
}
.cdm-hero-label {
  font-size: 0.75rem;
  color: var(--text-color-secondary, #64748b);
  margin-bottom: 0.2rem;
  font-weight: 500;
}
.cdm-hero-label strong {
  color: var(--text-color, #1e293b);
  font-weight: 600;
}
.dark .cdm-hero-label strong {
  color: #e4e4e7;
}
.cdm-hero-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--primary-color, #3b82f6);
  line-height: 1.2;
}
.dark .cdm-hero-value {
  color: #60a5fa;
}
.cdm-hero-unit {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  margin-left: 0.3rem;
}
.dark .cdm-hero-unit {
  color: #71717a;
}
.cdm-hero-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.cdm-hero-bar-track {
  flex: 1;
  height: 5px;
  background: var(--surface-border, #e2e8f0);
  border-radius: 999px;
  overflow: hidden;
}
.dark .cdm-hero-bar-track {
  background: rgba(255,255,255,0.08);
}
.cdm-hero-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 999px;
  transition: width 0.3s ease;
  min-width: 2px;
}
.cdm-hero-bar-fill.is-yellow {
  background: #d97706;
}
.cdm-hero-bar-fill.is-red {
  background: #ef4444;
}
.cdm-hero-bar-fill:not(.is-yellow):not(.is-red) {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.cdm-hero-bar-pct {
  font-size: 0.68rem;
  font-weight: 600;
  color: #10b981;
  white-space: nowrap;
  flex-shrink: 0;
}
.dark .cdm-hero-bar-pct {
  color: #6ee7b7;
}
.cdm-hero-bar-pct.is-yellow {
  color: #d97706;
}
.cdm-hero-bar-pct.is-red {
  color: #ef4444;
}


/* ── Hero strip (3 metricas) ── */
.cdm-hero-strip {
  display: flex;
  align-items: center;
  gap: 0;
  margin-top: 0.75rem;
  padding-top: 0.65rem;
  border-top: 1px solid rgba(16,185,129,0.12);
}
.dark .cdm-hero-strip {
  border-color: rgba(16,185,129,0.08);
}
.cdm-hero-stat {
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.cdm-hero-stat-val {
  display: block;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-color, #1e293b);
  line-height: 1.3;
}
.dark .cdm-hero-stat-val {
  color: #e4e4e7;
}
.cdm-hero-stat-val.is-green { color: #22c55e; }
.cdm-hero-stat-val.is-yellow { color: #d97706; }
.cdm-hero-stat-val.is-red { color: #ef4444; }
.cdm-hero-stat-lbl {
  display: block;
  font-size: 0.62rem;
  font-weight: 500;
  color: var(--text-color-secondary, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.dark .cdm-hero-stat-lbl {
  color: #71717a;
}
.cdm-hero-stat-divider {
  width: 1px;
  height: 28px;
  background: var(--surface-border, #e2e8f0);
  flex-shrink: 0;
}
.dark .cdm-hero-stat-divider {
  background: rgba(255,255,255,0.08);
}

/* ── Insight bar ── */
.cdm-insight-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.45rem 0.7rem;
  margin-bottom: 0.65rem;
  background: var(--surface-ground, #f8fafc);
  border: 1px solid var(--surface-border, #e2e8f0);
  border-radius: 8px;
}
.dark .cdm-insight-bar {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.06);
}
.cdm-insight-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--text-color-secondary, #64748b);
}
.dark .cdm-insight-item {
  color: #a1a1aa;
}
.cdm-insight-item.is-warning {
  color: #d97706;
}
.dark .cdm-insight-item.is-warning {
  color: #fbbf24;
}
.cdm-insight-item.is-success {
  color: #22c55e;
}
.dark .cdm-insight-item.is-success {
  color: #4ade80;
}
.cdm-insight-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cdm-insight-dot.is-red { background: #ef4444; }
.cdm-insight-dot.is-yellow { background: #d97706; }
.cdm-insight-dot.is-green { background: #22c55e; }

/* ── Sort select ── */
.cdm-sort-select {
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--surface-border, #e2e8f0);
  background: var(--surface-card, #fff);
  color: var(--text-color, #1e293b);
  font-size: 0.72rem;
  font-family: inherit;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s;
  flex-shrink: 0;
  min-width: 120px;
}
.dark .cdm-sort-select {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.1);
  color: #e4e4e7;
}
.cdm-sort-select:focus {
  border-color: var(--primary-color, #3b82f6);
}
.cdm-toolbar-row--filters {
  gap: 0.4rem;
  flex-wrap: wrap;
}

/* ── Group sections (Criticos / Riesgo / Optimos) ── */
.cdm-group-section {
  margin-bottom: 0.65rem;
}
.cdm-group-section:last-child {
  margin-bottom: 0;
}
.cdm-group-section-header {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  margin-bottom: 0.35rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 6px;
}
.cdm-group-chevron {
  font-size: 0.7rem;
  color: var(--text-color-secondary, #94a3b8);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.dark .cdm-group-chevron {
  color: #71717a;
}
.cdm-group-chevron.is-collapsed {
  transform: rotate(-90deg);
}

.cdm-group-section-header.is-critico {
  color: #b91c1c;
  background: rgba(239,68,68,0.06);
}
.dark .cdm-group-section-header.is-critico {
  color: #fca5a5;
  background: rgba(239,68,68,0.1);
}
.cdm-group-section-header.is-riesgo {
  color: #92400e;
  background: rgba(217,119,6,0.06);
}
.dark .cdm-group-section-header.is-riesgo {
  color: #fcd34d;
  background: rgba(217,119,6,0.1);
}
.cdm-group-section-header.is-optimo {
  color: #15803d;
  background: rgba(34,197,94,0.06);
}
.dark .cdm-group-section-header.is-optimo {
  color: #86efac;
  background: rgba(34,197,94,0.1);
}
.cdm-group-section-count {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: 400;
  opacity: 0.6;
}

/* ── Channel health bar-left indicator ── */
.cdm-channel-bar-left {
  width: 3px;
  height: 32px;
  border-radius: 2px;
  flex-shrink: 0;
  align-self: center;
}
.cdm-channel-bar-left.is-red { background: #ef4444; }
.cdm-channel-bar-left.is-yellow { background: #d97706; }
.cdm-channel-bar-left.is-green { background: #22c55e; }

/* ── Health-based card left borders ── */
.cdm-card--critico {
  border-left: 3px solid #ef4444 !important;
}
.cdm-card--riesgo {
  border-left: 3px solid #d97706 !important;
}
.cdm-card--optimo {
  border-left: 3px solid #22c55e !important;
}

/* ── Health-based bar fill colors ── */
.cdm-channel-bar-fill.is-red { background: #ef4444; }
.dark .cdm-channel-bar-fill.is-red { background: #f87171; }
.cdm-channel-bar-fill.is-yellow { background: #d97706; }
.dark .cdm-channel-bar-fill.is-yellow { background: #fbbf24; }
.cdm-channel-bar-fill.is-green { background: #22c55e; }
.dark .cdm-channel-bar-fill.is-green { background: #4ade80; }

/* ── Health-based text colors ── */
.cdm-channel-count.is-red { color: #ef4444; }
.dark .cdm-channel-count.is-red { color: #f87171; }
.cdm-channel-count.is-yellow { color: #d97706; }
.dark .cdm-channel-count.is-yellow { color: #fbbf24; }
.cdm-channel-count.is-green { color: #22c55e; }
.dark .cdm-channel-count.is-green { color: #4ade80; }

.cdm-channel-pct.is-red { color: #ef4444; }
.dark .cdm-channel-pct.is-red { color: #f87171; }
.cdm-channel-pct.is-yellow { color: #d97706; }
.dark .cdm-channel-pct.is-yellow { color: #fbbf24; }

.cdm-card-sub--health {
  font-weight: 600 !important;
  font-size: 0.68rem !important;
  text-transform: capitalize;
}
.cdm-card-sub--critico { color: #ef4444 !important; }
.dark .cdm-card-sub--critico { color: #f87171 !important; }
.cdm-card-sub--riesgo { color: #d97706 !important; }
.dark .cdm-card-sub--riesgo { color: #fbbf24 !important; }
.cdm-card-sub--optimo { color: #22c55e !important; }
.dark .cdm-card-sub--optimo { color: #4ade80 !important; }

/* ── Grid health-based top borders ── */
.cdm-grid-card--critico {
  border-top: 3px solid #ef4444 !important;
}
.cdm-grid-card--riesgo {
  border-top: 3px solid #d97706 !important;
}
.cdm-grid-card--optimo {
  border-top: 3px solid #22c55e !important;
}

/* Grid card bar fills */
.cdm-grid-card-bar-fill.is-red { background: #ef4444; }
.dark .cdm-grid-card-bar-fill.is-red { background: #f87171; }
.cdm-grid-card-bar-fill.is-yellow { background: #d97706; }
.dark .cdm-grid-card-bar-fill.is-yellow { background: #fbbf24; }
.cdm-grid-card-bar-fill.is-green { background: #22c55e; }
.dark .cdm-grid-card-bar-fill.is-green { background: #4ade80; }

.cdm-grid-card-pct.is-red { color: #ef4444; }
.dark .cdm-grid-card-pct.is-red { color: #f87171; }
.cdm-grid-card-pct.is-yellow { color: #d97706; }
.dark .cdm-grid-card-pct.is-yellow { color: #fbbf24; }
.cdm-grid-card-pct.is-green { color: #22c55e; }
.dark .cdm-grid-card-pct.is-green { color: #4ade80; }

.cdm-grid-card-sub--health {
  font-weight: 600 !important;
  font-size: 0.68rem !important;
  text-transform: capitalize;
}
.cdm-grid-card-sub--health.is-red { color: #ef4444 !important; }
.dark .cdm-grid-card-sub--health.is-red { color: #f87171 !important; }
.cdm-grid-card-sub--health.is-yellow { color: #d97706 !important; }
.dark .cdm-grid-card-sub--health.is-yellow { color: #fbbf24 !important; }
.cdm-grid-card-sub--health.is-green { color: #22c55e !important; }
.dark .cdm-grid-card-sub--health.is-green { color: #4ade80 !important; }

/* Grid card count */
.cdm-grid-card-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-color-secondary, #64748b);
  margin-bottom: 0.25rem;
}
.dark .cdm-grid-card-count {
  color: #a1a1aa;
}

/* ── Bar footer ── */
.cdm-channel-bar-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.2rem;
  margin-top: 1px;
}

/* ── Unidentified device ── */
.cdm-card--device.is-unidentified {
  border-left: 3px solid #ef4444;
}
.cdm-card--device.is-paused {
  border-left: 3px solid #d97706;
}
.cdm-badge--unidentified {
  display: inline-block;
  background: #fef2f2;
  color: #ef4444;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #fecaca;
  flex-shrink: 0;
}
.dark .cdm-badge--unidentified {
  background: #451a1a;
  color: #f87171;
  border-color: #7f1d1d;
}
.cdm-missing-fields {
  font-size: 0.7rem;
  font-weight: 500;
  color: #ef4444;
  flex-shrink: 0;
}
.dark .cdm-missing-fields {
  color: #f87171;
}
.cdm-missing-fields-meta {
  font-size: 0.6rem;
  font-weight: 500;
  color: #ef4444;
  opacity: 0.6;
  flex-shrink: 0;
}
.dark .cdm-missing-fields-meta {
  color: #f87171;
}
.cdm-group-header.is-critico {
  color: #b91c1c;
  background: rgba(239,68,68,0.06);
  border-radius: 6px;
}
.dark .cdm-group-header.is-critico {
  color: #fca5a5;
  background: rgba(239,68,68,0.1);
}
.cdm-group-header.is-riesgo {
  color: #92400e;
  background: rgba(217,119,6,0.06);
  border-radius: 6px;
}
.dark .cdm-group-header.is-riesgo {
  color: #fcd34d;
  background: rgba(217,119,6,0.1);
}
.cdm-group-detail {
  font-weight: 400;
  font-size: 0.65rem;
  opacity: 0.7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Responsive ── */
@media (max-width: 640px) {
.cdm-body {
    padding: 1rem 1rem 1.25rem;
  }
  .cdm-breadcrumb {
    padding: 0.5rem 1rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .cdm-card-body {
    flex-wrap: wrap;
  }
  .cdm-chip-group {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
  }
}
</style>
